import { extractSeqIdNumber } from "../../helpers/utils";
import ReactHtmlParser from "react-html-parser";
import React, { useEffect, useState, useRef } from "react";
import DiscoverService from "../../services/discoverService";
import _, { result } from "lodash";
import { makeStyles } from "@material-ui/core/styles";
import DiscoverAnnotationModal from "../../shared/Modal/DiscoverAnnotationModal";
import DiscoverReportDataIssueModal from "../../shared/Modal/DiscoverReportDataIssueModal";
import CreateOutlinedIcon from '@material-ui/icons/CreateOutlined';
import "./ResultTableRecDetails.css"
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BsStars } from "react-icons/bs";
import Tooltip from "@material-ui/core/Tooltip";
import { FiFileText } from "react-icons/fi";
import { useSelector } from 'react-redux';
import { FaStar, FaRegFilePdf, FaInfoCircle } from "react-icons/fa";
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { openDetailsInNewPage as openDetailsInNewPageUtil } from './openDetailsInNewPage';
import AlignmentChart, { ClassicAlignmentChart } from "./AlignmentChart";
import SeqIdAlignmentPopup from "./SeqIdAlignmentPopup";
import SeqIdTable from "./SeqIdTable";

const lightenHex = (hex, amount = 0.7) => {
    if (!hex || !hex.startsWith('#')) return hex;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgb(${Math.round(r + (255 - r) * amount)}, ${Math.round(g + (255 - g) * amount)}, ${Math.round(b + (255 - b) * amount)})`;
};

const toNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

const toChartItem = (doc) => {
    const QUERY_L = toNumber(doc?.QUERY_L);
    const RESULT_OOBQ = toNumber(doc?.RESULT_OOBQ);
    const RESULT_OOEQ = toNumber(doc?.RESULT_OOEQ);
    const SUBJECT_L = toNumber(doc?.SUBJECT_L);
    const RESULT_OOBD = toNumber(doc?.RESULT_OOBD);
    const RESULT_OOED = toNumber(doc?.RESULT_OOED);

    if ([QUERY_L, RESULT_OOBQ, RESULT_OOEQ, SUBJECT_L, RESULT_OOBD, RESULT_OOED].some(v => v === null)) {
        return null;
    }
    
    return {
        id: String(doc?.RESULT_ID || doc?.dbkey || doc?.QUERY_ID || ''),
        query_id: doc?.QUERY_ID || '',
        result_rid: doc?.RESULT_RID || '',
        result_riq: toNumber(doc?.RESULT_RIQ),
        RESULT_GRAPHIC: {
            QUERY_L,
            RESULT_OOBQ,
            RESULT_OOEQ,
            SUBJECT_L,
            RESULT_OOBD,
            RESULT_OOED
        }
    };
};

const buildSubjectChartData = (allDocs, currentDoc) => {
    //console.log('Building chart data for currentDoc:', Object.keys(allDocs).length);
    if (!allDocs || !currentDoc) return [];

    const currentSubjectN = currentDoc.SUBJECT_N;
    const currentSubjectId = currentDoc.SUBJECT_ID;

    // Filter rows matching subject
    const rows = Object.values(allDocs).filter((doc) => {
        if (!doc) return false;
        
        if (currentSubjectN && doc.SUBJECT_N && doc.SUBJECT_N === currentSubjectN){
        return true;}
        if (currentSubjectId && doc.SUBJECT_ID && doc.SUBJECT_ID === currentSubjectId) return true;
       
    });


    const items = rows.map(toChartItem).filter(Boolean);
    if (items.length) return items;

    const fallback = toChartItem(currentDoc);
    return fallback ? [fallback] : [];
};

// Module-level — survives component remounts caused by key-based navigation.
// Per-subject cache: subjectId → chartRows[]. Grows as user browses subjects.
const _subjectCache = new Map();
const _prefetchScheduled = new Set(); // IDs already sent in a batch prefetch — skip on re-mount
let _cacheFilterKey = null;
let _allHitsLoaded = false; // true once the all-hits file has been fully loaded into cache
let _allHitsCapped = false; // true when file exceeded threshold — 15k loaded, rest batch per page

function getFilterKey(callData) {
    return [
        callData.workflowId,
        callData.userId,
        JSON.stringify(callData.legalStatuses || []),
        JSON.stringify(callData.authorities || []),
        JSON.stringify(callData.databases || []),
        callData.deduplication || ''
    ].join('|');
}

// Called eagerly from Discover.js once window.$javaCall is set so the cache is
// ready before the user clicks the first row. The component's own useEffect
// still runs but its guard (_allHitsLoaded check) makes it a no-op when this
// has already finished.
export function warmAllHitsCache(callData) {
    if (!callData) return;
    const filterKey = getFilterKey(callData);

    if ((_allHitsLoaded || _allHitsCapped) && _cacheFilterKey === filterKey) return;

    if (_cacheFilterKey !== filterKey) {
        _subjectCache.clear();
        _prefetchScheduled.clear();
        _allHitsLoaded = false;
        _allHitsCapped = false;
        _cacheFilterKey = filterKey;
    }

    DiscoverService.getAllSubjectSequenceHits(callData, callData.gqApiKey)
        .then(response => {
            if (_cacheFilterKey !== filterKey) return;
            const subjects = response?.subjects || [];
            if (subjects.length > 0) {
                subjects.forEach(subject => {
                    const chartRows = (subject.queryHits || []).map(mapHitToChartRow);
                    _subjectCache.set(subject.subjectId, chartRows);
                });
                if (response?.capped) {
                    _allHitsCapped = true;
                    console.log(`[AllHitsCache] Eager warm-up: file capped — ${subjects.length} subjects loaded`);
                } else {
                    _allHitsLoaded = true;
                    console.log(`[AllHitsCache] Eager warm-up: ${subjects.length} subjects loaded`);
                }
            }
        })
        .catch(() => {});
}

function mapHitToChartRow(hit) {
    return {
        id: hit.queryId,
        query_id: hit.queryId,
        result_riq: hit.riq,
        result_rid: hit.rid,
        RESULT_NFD: hit.resultNfd !== undefined ? hit.resultNfd : hit.nfd,
        RESULT_NFQ: hit.resultNfq !== undefined ? hit.resultNfq : hit.nfq,
        RESULT_GRAPHIC: {
            QUERY_L: hit.queryL,
            RESULT_OOBQ: hit.oobq,
            RESULT_OOEQ: hit.ooeq,
            SUBJECT_L: hit.subjectL,
            RESULT_OOBD: hit.oobd,
            RESULT_OOED: hit.ooed
        }
    };
}

function AlignmentHtmlWithTooltip({ html }) {
    const [tooltipTitle, setTooltipTitle] = useState('');
    const [tooltipAnchorEl, setTooltipAnchorEl] = useState(null);

    const handleMouseMove = (event) => {
        const square = event.target?.closest?.('.cm-mod-square[data-cm-tooltip]');
        const nextTitle = square?.getAttribute('data-cm-tooltip') || '';
        if (square !== tooltipAnchorEl) {
            setTooltipAnchorEl(square || null);
        }
        if (nextTitle !== tooltipTitle) {
            setTooltipTitle(nextTitle);
        }
    };

    return (
        <Tooltip
            arrow
            title={tooltipTitle}
            placement="top"
            open={Boolean(tooltipTitle) && Boolean(tooltipAnchorEl)}
            PopperProps={{ anchorEl: tooltipAnchorEl }}
            disableFocusListener
            disableHoverListener
            disableTouchListener
        >
            <span
                onMouseMove={handleMouseMove}
                onMouseLeave={() => {
                    setTooltipTitle('');
                    setTooltipAnchorEl(null);
                }}
                dangerouslySetInnerHTML={{ __html: html }}
            />
        </Tooltip>
    );
}

const useStyles = makeStyles((theme) => ({
    loginSubmitCancel: {
        backgroundColor: '#0182C5',
        borderColor: '#1F4E79',
        border: '1px solid #1F4E79',
        color: 'white',
        margin: '4px',
        textTransform: 'capitalize',
        '&:hover': {
            backgroundColor: '#0182C5',
            boxShadow: 'none',
        },
    },
    menuList: {
        paddingTop: '0px !important',
        paddingBottom: '0px !important',
    },
    anchorTag: {
        textDecoration: 'none',
        color: 'inherit',
    },
}));

const QID_INFO = ['QUERY_ID', 'QUERY_L', 'QUERY_DE', 'RESULT_RIQ', 'RESULT_FQ', 'RESULT_GAPQ'];
const SID_INFO = ['SUBJECT_ID', 'SUBJECT_AC', 'SUBJECT_SI', 'SUBJECT_L', 'RESULT_RID', 'RESULT_NFD', 'RESULT_GAPD'];
const CAS_RGGROUP = ['SUBJECT_RG', 'SUBJECT_RB', 'SUBJECT_RC'];
const CAS_CDGROUP = ['SUBJECT_CD', 'SUBJECT_CK', 'SUBJECT_CL'];
const GXGROUP = ['SUBJECT_GX', 'UFS_DISPLAY', 'SUBJECT_PJ'];
const PCT_GROUP = ['SUBJECT_P4', 'SUBJECT_P5', 'SUBJECT_P8'];

const GROUPS = ['DOCHEADER', 'DOCLINKS', 'SUBJECT_FAMINFO', 'SUBJECT_P1', 'SUBJECT_PC', 'SUBJECT_CT', 'SUBJECT_Z3', 'SUBJECT_CC', 'SUBJECT_SEQIDNOSUMM',
    'ASSIGNEES', 'PATENT_DATES', 'LEGAL_STATUS', 'ORGANISM', 'QUERY_ALI_INFO', 'SUBJECT_ALI_INFO', 'ALI_INFO', 'UFS_DISPLAY',
    'CAS_RGGROUP', 'CAS_CDGROUP', 'GXGROUP', 'PCT_GROUP', 'SUBJECT_ZA', 'SUBJECT_ZB', 'SUBJECT_ZC', 'SUBJECT_ZD', 'SUBJECT_ZE', 'SUBJECT_ZF'];

// AI-generated fields: intentionally NOT part of fieldConfig's detailViewConfig/getFullRecordFields,
// since that would also surface them in the ungated 'GQPAT Data' column bucket (ResultTable.js
// getGqpatColumns()). Kept as an explicit allowlist so they're only ever reachable through the
// license-gated 'AI Summary' bucket (see ResultTable.js current_user.aiAccess check).
// NOTE: SUBJECT_ZG/SUBJECT_ZH (parent confidence/reasoning) are deliberately excluded here —
// per the AI annotations guide §7-8 they're satellite values of SUBJECT_ZF (normalized parent),
// rendered inline inside ZF's row in the ASSIGNEES section, not as their own detail-panel section.
// SUBJECT_ZF itself is also held out of this allowlist for now — AI Normalized Parent is on
// hold for a later release, so keeping it out of `columns`/`uniqueColumns` here hides its
// ASSIGNEES-section row below without deleting that rendering code.
const AI_SUMMARY_FIELDS = ['SUBJECT_ZA', 'SUBJECT_ZB', 'SUBJECT_ZC', 'SUBJECT_ZD', 'SUBJECT_ZE'];

// Splits SUBJECT_ZC ("**Technical Problem** ... **Inventive Solution** ...", the two
// bold-wrapped labels concatenated with no delimiter) into separate markdown paragraphs
// so the two sections don't render smudged together. See ai-annotations guide §4.
function formatAiSummary(raw) {
    if (!raw) return '';
    return raw.replace(/\s*\*\*(Technical Problem|Inventive Solution)\*\*\s*/gi,
        (match, label, offset) => `${offset === 0 ? '' : '\n\n'}**${label}** `);
}

// Parses SUBJECT_ZD ("[title / range] body ...", groups concatenated with no delimiter
// other than the next "[") into one object per claim group. See ai-annotations guide §5.
function parseAiClaimsAnalysis(raw) {
    if (!raw) return [];
    return raw.split(/(?=\[)/).filter(s => s.trim()).map(block => {
        const headerMatch = block.match(/^\[([^\]]+)\]\s*([\s\S]*)/);
        if (!headerMatch) return { title: '', claimRange: '', body: block.trim() };
        const [title, range] = headerMatch[1].split(' / ');
        return {
            title: (title || '').trim(),
            claimRange: (range || '').trim(),
            body: headerMatch[2].trim(),
        };
    });
}

// Parses SUBJECT_ZE ("SEQ ID NO n (type): desc -- Claims: list ...", entries concatenated
// with no delimiter other than the next "SEQ ID NO") into one object per sequence.
// See ai-annotations guide §6.
function parseAiSeqIdSummary(raw) {
    if (!raw) return [];
    return raw.split(/(?=SEQ ID NO \d)/).filter(s => s.trim()).map(entry => {
        const m = entry.match(/SEQ ID NO (\d+)\s+\(([^)]+)\):\s*(.+?)\s+--\s+Claims:\s*(.+)/);
        if (!m) return { seq_id_no: null, sequence_type: '', description: entry.trim(), claims: '' };
        return {
            seq_id_no: m[1].trim(),
            sequence_type: m[2].trim(),
            description: m[3].trim(),
            claims: m[4].trim(),
        };
    });
}

export default function ResultTableRecDetail({ data, item, idx, fieldConfig, filter, annotState, highlight, toggleFullRecordExpand, refreshAnnotState,
    expandAllDetailPage, expandAllClaims, expandClaim, showLessClaim, expandAllAlignments, expandAlignment,
    fromItemsPage, groupValue1, groupValue2, saveAnnotation, parentAnnotState, isAbWorkflow,
    setFtWindowsToOpen, displayedColumns,
    currentVisibleSection, setCurrentVisibleSection, isCmNoSeq, selectedQueryId: externalSelectedQueryId }) {
    annotState = annotState ?? {};
    annotState.notes ??= {};
    annotState.dbkeys ??= {};
    annotState.stars ??= {};
    annotState.colors ??= {};
    annotState.claimsCollapse ??= {};
    annotState.claimsCollapseLess ??= {};
    annotState.alignmentsCollapse ??= {};

    parentAnnotState = parentAnnotState ?? {};
    parentAnnotState.stars ??= {};
    parentAnnotState.colors ??= {};

    const [selectedItem, setSelectedItem] = useState(item);
    // State to hold translated abstract and translation toggle
    const [translatedAbstract, setTranslatedAbstract] = useState(null);
    const [showTranslation, setShowTranslation] = useState(true);
    const [selectedLanguage, setSelectedLanguage] = useState('');
    const [translating, setTranslating] = useState(false);
    const [translatedLanguage, setTranslatedLanguage] = useState('');
    const [copiedTranslation, setCopiedTranslation] = useState(false);
    const [claimTreeText, setClaimTreeText] = useState(null);
    const [showClaimTree, setShowClaimTree] = useState(false);
    const [loadingClaimTree, setLoadingClaimTree] = useState(false);
    const [selectedQueryId, setSelectedQueryId] = useState(null);
    const [highlightedQueryId, setHighlightedQueryId] = useState(null);
    const [alignmentColorMode, setAlignmentColorMode] = useState(
        () => localStorage.getItem('alignmentColorMode') === 'true'
    );
    const [multiHitMode, setMultiHitMode] = useState(
        () => localStorage.getItem('multiHitMode') !== 'false'
    );
    const [subjectHitsLoading, setSubjectHitsLoading] = useState(false);
    const [subjectHitsChartRows, setSubjectHitsChartRows] = useState(null);
    const [tableState, setTableState] = useState(annotState);
    const alignmentChartRows = React.useMemo(() => {
        const currentDoc = data?.docItems?.[selectedItem?.dbkey];
        return buildSubjectChartData(data?.docItems, currentDoc);
    }, [data?.docItems, selectedItem?.dbkey]);

    const currentSubjectSeqId = React.useMemo(() => {
        const currentDoc = data?.docItems?.[selectedItem?.dbkey];
        return currentDoc?.SUBJECT_ID || currentDoc?.SUBJECT_N || null;
    }, [data?.docItems, selectedItem?.dbkey]);

    const HIGHLIGHT_COLORS = [
        'FFEB3B', 'FFA726', 'EF9A9A', 'B39DDB', '80CBC4', 'F48FB1', '81D4FA',
        'A5D6A7', 'E6EE9C', 'FFF59D', 'FFE082', 'FFCC80', 'FFAB91',
        'FF7043', 'BCAAA4', 'B0BEC5'
    ];
    const queryColorMap = React.useMemo(() => {
        const entries = filter?.state?.smartHighlightFilter?.['QUERY_ID'] || [];
        const map = {};
        entries.forEach(entry => {
            if (entry.text && entry.color != null) {
                map[entry.text.toUpperCase()] = '#' + HIGHLIGHT_COLORS[entry.color];
            }
        });
        return map;
    }, [filter?.state?.smartHighlightFilter]);
    const chartCoverageText = React.useMemo(() => {
        const currentDoc = data?.docItems?.[selectedItem?.dbkey];
        const subjectLabel = currentDoc?.SUBJECT_N || 'this subject';
        const loadedHits = alignmentChartRows.length;
        if (!loadedHits) {
            return `Chart not available: no loaded rows for ${subjectLabel} in current result payload.`;
        }
        const suffix = loadedHits === 1 ? '' : 's';
        return `Chart scope: ${loadedHits} loaded query hit${suffix} for ${subjectLabel} from current data.docItems payload only.`;
    }, [alignmentChartRows, data?.docItems, selectedItem?.dbkey]);
    React.useEffect(() => {
        if (selectedItem && annotState && annotState.alignmentsCollapse) {
            annotState.alignmentsCollapse[selectedItem.dbkey] = false;
            refreshAnnotState(annotState);
        }
        if (selectedItem && annotState && annotState.claimsCollapse) {
            annotState.claimsCollapse[selectedItem.dbkey] = false;
            refreshAnnotState(annotState);
        }
    }, [selectedItem]);


    React.useLayoutEffect(() => {
        if (!currentVisibleSection) return;

        requestAnimationFrame(() => {
            const el = document.getElementById(selectedItem.dbkey + '_details_' + currentVisibleSection);
            const container = document.querySelector('.detail-container');
            const header = document.querySelector('.sticky-docheader');
            const headerHeight = header ? header.offsetHeight : 0;

            if (el && container) {
                isScrollingProgrammatically.current = true;
                const y = el.offsetTop - headerHeight;
                container.scrollTo({ top: y, behavior: 'auto' });

                setTimeout(() => {
                    isScrollingProgrammatically.current = false;
                }, 50);
            }
        });
    }, [selectedItem.dbkey, currentVisibleSection]);
    // To Update Text Notes
    const [updateNotes, setUpdateNotes] = useState(false);
    const notesRef = useRef(annotState.notes[selectedItem.dbkey]);
    const classes = useStyles();

    // To batch update notes
    const [showAnnotationModal, setShowAnnotationModal] = useState(false);
    const [key, setKey] = useState('');
    const [type, setType] = useState('');
    const [value, setValue] = useState(0);
    let singleClickTimer = null;
    const [annotGroup, setAnnotGroup] = useState('');
    const [annotGroupDisplay, setAnnotGroupDisplay] = useState('');
    const [annotSubjectId, setAnnotSubjectId] = useState('');
    const [annotSubjectPn, setAnnotSubjectPn] = useState('');

    // Report data issue
    const [reportModal, setReportModal] = useState(false);
    const [reportPn, setReportPn] = useState('');

    // Scroll position tracking
    const containerRef = useRef(null);
    const isScrollingProgrammatically = useRef(false);

    const toggle = useSelector(state => state.setToggle.toggle);
    const [showLinksSection, setShowLinksSection] = useState(false);

    const [showColor, setShowColor] = useState(false);

    //Linkout menu
    const [linkoutAnchorEl, setLinkoutAnchorEl] = useState(null);
    const isLinkoutMenu = Boolean(linkoutAnchorEl);

    const handleLinkoutMenuClosed = () => {
        setLinkoutAnchorEl(null);
    };

    const handleLinkoutMenuOpen = (event) => {
        setLinkoutAnchorEl(event.currentTarget);
    };

    const updateNewNotes = async (name) => {
        let notes = decodeURIComponent(name);
        notesRef.current = notes;
        annotState.notes[selectedItem.dbkey] = notes;
        refreshAnnotState(annotState);

        let selectedIds = [];
        selectedIds.push(annotState.dbkeys[selectedItem.dbkey]);
        let call = window.$javaCall;
        call.data['selectedIds'] = selectedIds;
        call.data['allPages'] = false;
        call.data['comment'] = notes;
        call.data['annotType'] = 'TN';
        DiscoverService.saveAnnotation(call.data, call.data.gqApiKey);

        setUpdateNotes(false);
    }
    const toggleStars = (e, key, nbStars, fieldConfig, item) => {
        if (singleClickTimer !== null) {
            clearTimeout(singleClickTimer);
            singleClickTimer = null;
            handleDoubleClick(key, 'star', annotState.stars[key] ? annotState.stars[key] : 0, fieldConfig, item);
        } else {
            singleClickTimer = setTimeout(() => {
                annotState.stars[key] = (annotState.stars[key] === nbStars) ? 0 : nbStars;
                if (fromItemsPage) {
                    parentAnnotState.stars[key] = annotState.stars[key];
                }
                setTableState({ ...annotState });
                saveSingleAnnotation('star', key, annotState.stars[key]);
                if (typeof refreshAnnotState === 'function') {
                    refreshAnnotState({ ...annotState });
                }
                clearTimeout(singleClickTimer);
                singleClickTimer = null;
            }, 300);
        }
    }

    const selectColor = (key, value, fieldConfig, item) => {
        setShowColor(false);
        annotState.colors[key] = value;
        tableState.colors[key] = value;
        if (fromItemsPage) {
            parentAnnotState.colors[key] = annotState.colors[key];
        }
        setTableState({ ...tableState });
        saveSingleAnnotation('color', key, value);
        if (typeof refreshAnnotState === 'function') {
            refreshAnnotState({ ...annotState });
        }
    }

    useEffect(() => {
        function handler(event) {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type !== 'SAVE_ANNOTATION') return;

            const { annotType, key, value } = event.data;
            saveSingleAnnotation(annotType, key, value);
        }

        window.addEventListener('message', handler);
        return () => {
            window.removeEventListener('message', handler);
        };
    }, []);


    const fetchSubjectHits = async (subjectSeqId) => {
        if (!subjectSeqId || !window.$javaCall) return;

        const call = window.$javaCall;
        const filterKey = getFilterKey(call.data);

        // Reset all cache state if filter/search changed
        if (_cacheFilterKey !== filterKey) {
            _subjectCache.clear();
            _prefetchScheduled.clear();
            _allHitsLoaded = false;
            _allHitsCapped = false;
            _cacheFilterKey = filterKey;
        }   

        // Cache hit — render instantly with no API call
        if (_subjectCache.has(subjectSeqId)) {
            const chartRows = _subjectCache.get(subjectSeqId);
            setSubjectHitsChartRows(chartRows.length ? chartRows : null);
            setSubjectHitsLoading(false);
            return;
        }

        setSubjectHitsLoading(true);
        setSubjectHitsChartRows(null);

        try {
            const response = await DiscoverService.getSubjectSequenceHits(
                { ...call.data, subjectSeqId }, call.data.gqApiKey
            );
            const chartRows = (response?.queryHits || []).map(mapHitToChartRow);
            _subjectCache.set(subjectSeqId, chartRows);
            //console.log(`[SubjectCache] size=${_subjectCache.size}`);   
            setSubjectHitsChartRows(chartRows.length ? chartRows : null);
        } catch (error) {
            console.error('Error fetching subject sequence hits:', error);
            setSubjectHitsChartRows(null);
        } finally {
            setSubjectHitsLoading(false);
        }
    };

    // Auto-fetch whenever the current subject changes — fires on mount
    // (component remounts via key={dbkey}) and on selectDatabase within a record.
    useEffect(() => {
        if (!currentSubjectSeqId) return;
        fetchSubjectHits(currentSubjectSeqId);
    }, [currentSubjectSeqId]);

    // On each page/subject navigation (component remounts via key={dbkey}):
    //   1. Try the all-hits file once — if ready, populate full cache and stop forever.
    //   2. If not ready — batch only the subjects NOT already in _subjectCache.
    //      Going back to a previously visited page skips the batch entirely since
    //      those subject IDs are already cached.
    useEffect(() => {
        if (!window.$javaCall) return;
        const call = window.$javaCall;
        const filterKey = getFilterKey(call.data);

        if (_allHitsLoaded && _cacheFilterKey === filterKey) return;

        const pageSubjectIds = [...new Set(
            Object.values(data?.docItems || {})
                .map(doc => doc.SUBJECT_ID || doc.SUBJECT_N)
                .filter(Boolean)
        )];

        const uncachedPageIds = pageSubjectIds.filter(id => !_subjectCache.has(id));
        if (uncachedPageIds.length === 0) {
            //console.log('[AllHitsCache] All current page subjects cached, skipping backend call');
            return;
        }

        // File was capped on a previous call — skip getAllSubjectSequenceHits (would return
        // the same 15k again) and go straight to batch for subjects not in cache.
        if (_allHitsCapped && _cacheFilterKey === filterKey) {
            console.log(`[AllHitsCache] File capped — batch loading ${uncachedPageIds.length} uncached subjects`);
            DiscoverService.getSubjectSequenceHitsBatch(call.data, uncachedPageIds, call.data.gqApiKey)
                .then(batchResponse => {
                    if (_cacheFilterKey !== filterKey) return;
                    (batchResponse?.subjects || []).forEach(subject => {
                        const chartRows = (subject.queryHits || []).map(mapHitToChartRow);
                        _subjectCache.set(subject.subjectId, chartRows);
                    });
                    //console.log(`[AllHitsCache] Batch done — ${_subjectCache.size} subjects in cache`);
                }).catch(() => {});
            return;
        }

        const startTime = Date.now();

        DiscoverService.getAllSubjectSequenceHits(
            call.data, call.data.gqApiKey
        ).then(response => {
            if (_cacheFilterKey !== filterKey) return;

            const subjects = response?.subjects || [];
            const fileSizeBytes = response?.fileSizeBytes;
            if (fileSizeBytes != null) {
                const fileSizeMb = (fileSizeBytes / (1024 * 1024)).toFixed(2);
                //console.log(`[AllHitsCache] allhits.json file size: ${fileSizeMb} MB (${fileSizeBytes} bytes)`);
            }

            if (subjects.length > 0) {
                subjects.forEach(subject => {
                    const chartRows = (subject.queryHits || []).map(mapHitToChartRow);
                    _subjectCache.set(subject.subjectId, chartRows);
                });
                const elapsed = ((Date.now() - startTime) / 1000).toFixed(1);
                const roughKb = (new Blob([JSON.stringify([..._subjectCache.entries()])]).size / 1024).toFixed(1);

                if (response?.capped) {
                    _allHitsCapped = true;
                    //console.log(`[AllHitsCache] File capped — loaded ${subjects.length} subjects in ${elapsed}s | cache: ${_subjectCache.size} entries, ~${roughKb} KB`);
                    // Batch-load any current page subjects that fell outside the 15k cap
                    const stillUncached = uncachedPageIds.filter(id => !_subjectCache.has(id));
                    if (stillUncached.length > 0) {
                        //console.log(`[AllHitsCache] Batch loading ${stillUncached.length} page subjects outside cap`);
                        DiscoverService.getSubjectSequenceHitsBatch(call.data, stillUncached, call.data.gqApiKey)
                            .then(batchResponse => {
                                if (_cacheFilterKey !== filterKey) return;
                                const loaded = batchResponse?.subjects || [];
                                loaded.forEach(subject => {
                                    const chartRows = (subject.queryHits || []).map(mapHitToChartRow);
                                    _subjectCache.set(subject.subjectId, chartRows);
                                });
                                //console.log(`[AllHitsCache] Batch done — ${loaded.length} subjects loaded | total cache: ${_subjectCache.size}`);
                            }).catch(() => {});
                    }
                } else {
                    _allHitsLoaded = true;
                    //console.log(`[AllHitsCache] File ready — loaded ${subjects.length} subjects in ${elapsed}s | cache: ${_subjectCache.size} entries, ~${roughKb} KB`);
                }
                return;
            }

            // File not ready — batch only the uncached subjects.
            //console.log(`[AllHitsCache] File not ready — batch loading ${uncachedPageIds.length} uncached subjects (${pageSubjectIds.length - uncachedPageIds.length} already cached)`);
            DiscoverService.getSubjectSequenceHitsBatch(
                call.data, uncachedPageIds, call.data.gqApiKey
            ).then(batchResponse => {
                if (_cacheFilterKey !== filterKey) return;
                (batchResponse?.subjects || []).forEach(subject => {
                    const chartRows = (subject.queryHits || []).map(mapHitToChartRow);
                    _subjectCache.set(subject.subjectId, chartRows);
                });
                //console.log(`[AllHitsCache] Batch done — ${_subjectCache.size} subjects in cache`);
            }).catch(() => {});

        }).catch(() => {
            console.log(`[AllHitsCache] getAllSubjectHits failed — batch loading ${uncachedPageIds.length} uncached subjects`);
            DiscoverService.getSubjectSequenceHitsBatch(
                call.data, uncachedPageIds, call.data.gqApiKey
            ).then(batchResponse => {
                if (_cacheFilterKey !== filterKey) return;
                const loaded = batchResponse?.subjects || [];
                loaded.forEach(subject => {
                    const chartRows = (subject.queryHits || []).map(mapHitToChartRow);
                    _subjectCache.set(subject.subjectId, chartRows);
                });
                //console.log(`[AllHitsCache] Batch done — ${loaded.length} subjects loaded | total cache: ${_subjectCache.size}`);
            }).catch(() => {});
        });
    }, []);

    // Force a fresh fetch for the current subject — clears the full cache
    const handleFindAlignments = () => {
        _subjectCache.clear();
        _prefetchScheduled.clear();
        fetchSubjectHits(currentSubjectSeqId);
    };

    const saveSingleAnnotation = (annotType, key, val) => {
        let call = window.$javaCall;
        let selectedIds = [];
        selectedIds.push(tableState.dbkeys[key]);
        call.data['selectedIds'] = selectedIds;
        call.data['allPages'] = false;
        let intVal = 0;
        if (typeof val === "boolean") {
            intVal = val ? 1 : 0;
        } else {
            intVal = val;
        }
        call.data['value'] = annotType === 'notes' ? 0 : intVal;
        call.data['annotType'] = annotType;
        call.data['comment'] = annotType === 'notes' ? val : '';   // For notes, we send the value as comment from the openDetailsInNewPage.js
        DiscoverService.saveAnnotation(call.data, call.data.gqApiKey);
        if (window.opener) {
            window.opener.postMessage(
                { type: 'ANNOTATION_UPDATE', annotType, key, value: val },
                window.location.origin
            );
        }
    }

    const showColorBox = (e, key) => {
        if (singleClickTimer !== null) {
            clearTimeout(singleClickTimer);
            singleClickTimer = null;
            handleDoubleClick(key, 'color', annotState.colors[key] ? annotState.colors[key] : 0, fieldConfig, item);
        } else {
            singleClickTimer = setTimeout(() => {
                //if (!tableState.showColorBoxes) {
                // tableState.showColorBoxes = {};
                //}
                setShowColor(true);
                setTableState({ ...tableState });
                clearTimeout(singleClickTimer);
                singleClickTimer = null;
            }, 300);
        }
    }

    const handleDoubleClick = (key, type, value, fieldConfig, item) => {
        setKey(key);
        setType(type);
        setValue(value);
        calAnnotGroup(fieldConfig, item);
        setShowAnnotationModal(true);
    }

    const hideColorBox = (key) => {
        setShowColor(false);
    }

    // const showUpdateNotes = (e, key, fieldConfig, item) => {
    //     if (singleClickTimer !== null) {
    //         clearTimeout(singleClickTimer);
    //         singleClickTimer = null;

    //         e.preventDefault();
    //         notesRef.current = annotState.notes[key];
    //         setUpdateNotes(true);
    //     } else {
    //         singleClickTimer = setTimeout(() => {
    //             clearTimeout(singleClickTimer);
    //             singleClickTimer = null;

    //             // Set parameters and open the pop up dialog
    //             setType('notes');
    //             setValue(annotState.notes[key]);
    //             setKey(key);
    //             calAnnotGroup(fieldConfig, item);
    //             setShowAnnotationModal(true);
    //         }, 300);
    //     }
    // }

    const showUpdateNotes = (e, key, fieldConfig, item) => {

        e.preventDefault();
        //notesRef.current = annotState.notes[key];
        setUpdateNotes(true);
        setType('notes');
        setValue(annotState.notes[key]);
        setKey(key);
        calAnnotGroup(fieldConfig, item);
        setShowAnnotationModal(true);


    }

    const calAnnotGroup = (fieldConfig, item) => {
        setAnnotSubjectId(item["SUBJECT_ID"] || '');
        setAnnotSubjectPn(item["SUBJECT_PN"] || '');
        if (!groupValue1) {
            let groupVal = "";
            let groupName = "";
            // Allow batch update annotations for normal groups except CAS/HSP groups
            switch (fieldConfig.tableDisplayMode) {
                case 'family':
                    groupVal = item["SUBJECT_PB"];
                    groupName = "Patent Family";
                    break;
                case 'document':
                    groupVal = item["SUBJECT_PN"];
                    groupName = "Patent Number";
                    break;
                case 'ufs':
                    groupVal = item["SUBJECT_PJ"];
                    groupName = "Unique Family Sequence";
                    break;
                /*case 'cas':
                    groupVal = item["SUBJECT_RG"];
                    groupName = "CAS Registry Number";
                    break;*/
                case 'ssv':
                    groupVal = item["SUBJECT_ID"];
                    groupName = "Subject Sequence";
                    break;
                case 'qs':
                    groupVal = item["QUERY_ID"];
                    groupName = "Query Sequence";
                    break;
                default:
                    break;
            }
            setAnnotGroup(groupVal);
            setAnnotGroupDisplay(groupName);
        }
    }

    const callBack = (type, choice, annotated, dbkey, value, annotState,
        filter, fieldConfig, fromItemsPage, groupValue1, groupValue2) => {
        saveAnnotation(type, choice, annotated, dbkey, value, annotState,
            filter, fieldConfig, fromItemsPage, groupValue1, groupValue2, parentAnnotState);
        closeModal();
    }

    const closeModal = () => {
        setShowAnnotationModal(false);
        setReportModal(false);
    }

    const reportDataIssue = (item) => {
        setReportPn(item.SUBJECT_ID);
        setReportModal(true);
    }

    useEffect(() => {
        // To draw the alignment chart, copied from "app.directive('alignmentChart', function (){})"
        let w = 600,
            //h = 45,
            barheight = 15, // how high is the bar we draw (px)
            spacer = 8; // how much space between query and subject (px)
        let chart = document.getElementById(selectedItem.dbkey + "-alignmentchart");
        let d = _.cloneDeep(item);
        if (d === undefined || d === null || chart === undefined || chart === null) {
            return;
        }
        // we are using HTML5 canvas here
        let canvas = chart.firstElementChild;
        let context = canvas.getContext('2d');
        // if rev complemented switch the vals
        if (d.RESULT_OOBD > d.RESULT_OOED) {
            let tmp = d.RESULT_OOED;
            d.RESULT_OOED = d.RESULT_OOBD;
            d.RESULT_OOBD = tmp;
        }
        let left_qry = d.RESULT_OOBQ - 1;
        let left_sbj = d.RESULT_OOBD - 1;
        let over_qry = d.RESULT_OOEQ - d.RESULT_OOBQ + 1;
        let over_sbj = d.RESULT_OOED - d.RESULT_OOBD + 1;
        let right_qry = d.QUERY_L - d.RESULT_OOEQ;
        let right_sbj = d.SUBJECT_L - d.RESULT_OOED;

        let left_max = ((left_qry > left_sbj) ? left_qry : left_sbj);
        let over_max = ((over_qry > over_sbj) ? over_qry : over_sbj);
        let right_max = ((right_qry > right_sbj) ? right_qry : right_sbj);
        let total = left_max + over_max + right_max;
        let scale = (w / total);

        // left and right side of the query
        context.fillStyle = '#66ADEA';
        context.fillRect(
            scale * (left_max - left_qry), // x
            0, // y
            scale * left_qry, // width
            barheight // height
        );
        context.fillRect(
            scale * (left_max + over_max), // x
            0, // y
            scale * right_qry, // width
            barheight // height
        );

        // left and right side of the subject
        context.fillStyle = '#CCCCCC';
        context.fillRect(
            scale * (left_max - left_sbj),
            barheight + spacer,
            scale * left_sbj,
            barheight
        );
        context.fillRect(
            scale * (left_max + over_max),
            barheight + spacer,
            scale * right_sbj,
            barheight
        );

        // query overlap region
        context.fillStyle = '#337ab7';
        context.fillRect(
            scale * left_max,
            0,
            scale * over_max,
            barheight
        );

        // subject overlap region
        context.fillStyle = '#999999';
        context.fillRect(
            scale * left_max,
            barheight + spacer,
            scale * over_max,
            barheight
        );
    }, []);

    function isSelectedDatabase(dbkey, dbId) {
        return data.docItemsSelected[dbkey] === dbId;
    }

    function selectDatabase(dbkey, name, value) {
        data.docItems[dbkey] = value;
        data.docItemsSelected[dbkey] = name;
        value.dbkey = dbkey;
        setSelectedItem(value);
    }

    function getWordAfterFulldocview(inputString) {
        const regex = /fulldocview\/(\w+)/;
        const match = inputString.match(regex);
        return match ? match[1] : null;
    }
    const openFtWindow = (thing) => {

        let data = {
            pn: getWordAfterFulldocview(thing.VT_FT),
            seqIds: extractSeqIdNumber(thing),
            kws: ""
        }
        setFtWindowsToOpen(data)
    }
    const menuId = 'link-menu';
    const renderMenu = (
        <Menu
            anchorEl={linkoutAnchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            id={menuId}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            open={isLinkoutMenu}
            onClose={handleLinkoutMenuClosed}
            classes={{ list: classes.menuList }}
            PaperProps={{
                style: {
                    marginTop: 40,
                },
            }}
        >
            {fieldConfig.getLinkoutMenuItems(selectedItem, handleLinkoutMenuClosed, classes).map((item, idx) =>
                item.component ? (
                    <MenuItem key={idx} onClick={handleLinkoutMenuClosed} style={toggle ? { backgroundColor: 'white' } : { backgroundColor: '#292929' }}>
                        {item.component}
                    </MenuItem>
                ) : (
                    <MenuItem key={idx} onClick={handleLinkoutMenuClosed} style={toggle ? { backgroundColor: 'white' } : { backgroundColor: '#292929' }}>
                        <a href={item.url} target="_blank" rel="noopener noreferrer" className={classes && classes.anchorTag}>
                            {item.label}
                        </a>
                    </MenuItem>
                )
            )}
        </Menu>

    );


    const openDetailsInNewPage = () => {
        openDetailsInNewPageUtil(selectedItem, data, fieldConfig, toggle, {
            filter,
            annotState,
            parentAnnotState,
            fromItemsPage,
            groupValue1,
            groupValue2,
            isAbWorkflow,
            isCmNoSeq,
            displayedColumns,
            idx,
            selectedQueryId: externalSelectedQueryId
        });
    };

   const columns = (
        displayedColumns && displayedColumns.length > 0
            ? displayedColumns.filter(col =>
                col !== 'DOCHEADER' &&
                (
                fieldConfig.getFullRecordFields(selectedItem.type, data.displayMode).includes(col) ||
                AI_SUMMARY_FIELDS.includes(col)
                )
            )
            : fieldConfig.getFullRecordFields(selectedItem.type, data.displayMode)
                .filter(col => col !== 'DOCHEADER')
        );
    const uniqueColumns = [...new Set(columns)];  // Ensure unique columns for SUBJECT_CC and others


    return (
        <div className="detail-container">

            <div className="clearfix">
                {selectedItem.type === 'doc' &&
                    <div className="pull-left">
                        {selectedItem.databases && Object.keys(selectedItem.databases).length <= 1 &&
                            <span>
                                {Object.keys(selectedItem.databases).map(
                                    (name) => (
                                        <span key={'span_' + name}>{fieldConfig.getDbBriefNameForDocDetail(name)}</span>
                                    )
                                )}
                            </span>
                        }
                        {item.databases && Object.keys(item.databases).length > 1 &&
                            <span>
                                {Object.keys(item.databases).map(
                                    (name, dbIdx) => (
                                        <span key={'span_' + name}>
                                            {dbIdx > 0 &&
                                                <span> | </span>
                                            }
                                            {isSelectedDatabase(selectedItem.dbkey, name) &&
                                                <span>{fieldConfig.getDbBriefNameForDocDetail(name)}</span>
                                            }
                                            {!isSelectedDatabase(selectedItem.dbkey, name) &&
                                                <a href="#" onClick={(e) => { selectDatabase(selectedItem.dbkey, name, item.databases[name]); e.preventDefault(); }}>{fieldConfig.getDbBriefNameForDocDetail(name)}</a>
                                            }
                                        </span>
                                    )
                                )}
                            </span>
                        }
                    </div>
                }


            </div>
            {/* {selectedItem.type === 'doc' &&
                <div><br /><br /></div>
            } */}
            {selectedItem.type && fieldConfig.getFullRecordFields(selectedItem.type, data.displayMode).includes('DOCHEADER') && (
                <div className="sticky-docheader" style={{
                    backgroundColor: toggle ? '#fff' : '#333',
                }}>
                    <div className="detail-header-container">
                        <div className="detail-header1">
                            <div style={{ width: '600px' }}>
                                <div className="detail-subheader">
                                    <div className="detail-subheader1">{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_PT', data.docItems[selectedItem.dbkey]['SUBJECT_PT'], true))}</div>
                                    <div className="clearfix ">
                                        <div className="left-stack">

                                            {(() => {
                                                const docItem = data.docItems[selectedItem.dbkey];
                                                const subjectKey = docItem['SUBJECT_PU'] ? 'SUBJECT_PU' : docItem['SUBJECT_PA'] ? 'SUBJECT_PA' : null;
                                                if (!subjectKey) return null;
                                                const rawHtml = highlight(
                                                    fieldConfig,
                                                    filter,
                                                    subjectKey,
                                                    docItem[subjectKey],
                                                    false
                                                );
                                                const plainText = rawHtml.replace(/<[^>]+>/g, "");
                                                const truncated =
                                                    plainText.length > 200 ? plainText.substring(0, 200) + "..." : plainText;

                                                return (
                                                    <Tooltip
                                                        title={
                                                            <div
                                                                style={{ maxWidth: 400, whiteSpace: "pre-wrap", lineHeight: 1.4 }}
                                                                dangerouslySetInnerHTML={{ __html: rawHtml }}
                                                            />
                                                        }
                                                        placement="top"
                                                        arrow
                                                    >
                                                        <div className="pull-left truncated-text">
                                                            {ReactHtmlParser(truncated)}
                                                        </div>
                                                    </Tooltip>
                                                );
                                            })()}

                                            {/* {data.docItems[selectedItem.dbkey]['SUBJECT_PZ'] && (() => {
                                                const docItem = data.docItems[selectedItem.dbkey];
                                                const rawHtml = highlight(fieldConfig, filter, 'SUBJECT_PZ', docItem['SUBJECT_PZ'], false);
                                                const plainText = rawHtml.replace(/<[^>]+>/g, "");
                                                const truncated =
                                                    plainText.length > 200 ? plainText.substring(0, 200) + "..." : plainText;

                                                return (
                                                    <Tooltip
                                                        title={
                                                            <div
                                                                style={{ maxWidth: 400, whiteSpace: "pre-wrap", lineHeight: 1.4 }}
                                                                dangerouslySetInnerHTML={{ __html: rawHtml }}
                                                            />
                                                        }
                                                        placement="top"
                                                        arrow
                                                    >
                                                        <div className="pull-left truncated-text">
                                                            <span className="detail-field">{fieldConfig.getFieldInfo('SUBJECT_PZ').title}</span>: {ReactHtmlParser(truncated)}
                                                        </div>
                                                    </Tooltip>
                                                );
                                            })()} */}

                                            {/* {selectedItem.SUBJECT_AN &&
                                        <div className="pull-left">
                                            <span className="detail-field">{fieldConfig.getFieldInfo('SUBJECT_AN').title}</span> : {selectedItem.SUBJECT_AN}
                                        </div>
                                    } */}

                                            <div >
                                                {data.docItems[selectedItem.dbkey]['SUBJECT_D1'] &&
                                                    <div className="pull-left " style={{ paddingRight: '50px' }}>
                                                        <span className="detail-field">{"Pub. Date"}</span> : {ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_D1', data.docItems[selectedItem.dbkey]['SUBJECT_D1'], false))}
                                                    </div>
                                                }
                                                {data.docItems[selectedItem.dbkey]['SUBJECT_PG'] &&
                                                    <div className="pull-left ">
                                                        <span className="detail-field">{"Ext. Legal Status"}</span> : {ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_PG', data.docItems[selectedItem.dbkey]['SUBJECT_PG'], false))}
                                                    </div>
                                                }
                                            </div>


                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="detail-subheader">
                                {selectedItem.SUBJECT_PN &&
                                    <div className="right-stack">
                                        <div className="pn-header">
                                            {selectedItem.SUBJECT_PN}
                                            {selectedItem.SUBJECT_KC && selectedItem.SUBJECT_KC !== '' &&
                                                <span>-{selectedItem.SUBJECT_KC}</span>
                                            }
                                        </div>
                                        <div className="link-header">

                                            {data.docItems[selectedItem.dbkey].SUBJECT_PN && data.docItems[selectedItem.dbkey].VT_FT &&
                                                <div className="pull-left pad-menu-item">
                                                    <Tooltip title="Open Full Text Viewer">
                                                        <span>
                                                            <FiFileText
                                                                className='ft-icon'
                                                                data-action="fulltext"
                                                                onClick={() => {
                                                                    openFtWindow(data.docItems[selectedItem.dbkey])
                                                                }}
                                                            /></span></Tooltip>
                                                </div>
                                            }
                                            <div>
                                                <Tooltip title="Download PDF">
                                                    <span>
                                                <FaRegFilePdf
                                                    className='ft-icon'
                                                    style={{ marginLeft: '10px' }}
                                                    data-action="pdf"
                                                    onClick={() => {
                                                        const url = fieldConfig.getPDFlink(data.docItems[selectedItem.dbkey]);
                                                        if (url) {
                                                            window.open(url, '_blank', 'noopener,noreferrer');
                                                        }
                                                    }}
                                                /></span></Tooltip>
                                            </div>
                                            

                                            <a
                                                href="#"
                                                style={{ color: '#337ab7', textDecoration: 'underline', cursor: 'pointer' }}
                                                onClick={e => {
                                                    e.preventDefault();
                                                    //setShowLinksSection(v => !v);
                                                    handleLinkoutMenuOpen(e);
                                                }}
                                                id="linkouts-trigger"
                                            >
                                                Linkouts
                                            </a>


                                        </div>
                                        <div className="report-header"
                                            // style={{
                                            //     background: selectedItem.SUBJECT_GNAME && selectedItem.SUBJECT_GNAME.startsWith('GQPAT_')
                                            //         ? '#f2f9fb'
                                            //         : '#fff'
                                            // }}
                                        >

                                            {selectedItem.SUBJECT_GNAME && selectedItem.SUBJECT_GNAME.startsWith('GQPAT_') &&
                                                <span> <a href="#" onClick={(e) => { reportDataIssue(selectedItem); e.preventDefault(); }}>Report Data Issue</a></span>
                                            }
                                        </div>
                                    </div>
                                }
                            </div>
                        </div>
                        <div className="detail-header1">
                            <div style={{ width: '600px' }}>
                                <div id="notes-section" className="detail-subheader">
                                    {(selectedItem.type === 'sin' || selectedItem.type === 'flat') &&
                                        <Tooltip title="Click to Add/Edit notes.">
                                            <div className="pull-left" style={{ backgroundColor: toggle ? '#f1f9ff' : '#333', display: 'flex', flexDirection: 'row-reverse', justifyContent: 'space-between' }}
                                            onClick={e => showUpdateNotes(e, selectedItem.dbkey, fieldConfig, selectedItem)}>
                                                <div style={{ display: 'flex', alignItems: 'center', padding: '10px' }}>
                                                    <CreateOutlinedIcon onClick={e => showUpdateNotes(e, selectedItem.dbkey, fieldConfig, selectedItem)}></CreateOutlinedIcon>
                                                </div>

                                                <span className={"text-notes1"} onClick={e => showUpdateNotes(e, selectedItem.dbkey, fieldConfig, selectedItem)}>
                                                    {annotState.notes[selectedItem.dbkey] && annotState.notes[selectedItem.dbkey].trim() !== ''
                                                        ? annotState.notes[selectedItem.dbkey]
                                                        : <span className="add-notes-label">Add Personal notes</span>}
                                                </span>


                                            </div></Tooltip>
                                    }
                                </div>
                            </div>

                            <div id="annotation-section" className="detail-subheader">
                                {(item['type'] === 'sin' || item['type'] === 'flat') &&
                                    <div className="annot-header" style={{backgroundColor: toggle ? '#f1f9ff' : '#333'}}>
                                        <div className="annot-subheader">

                                            <div>
                                                <Tooltip arrow title={"Annotate Records. Single click – single record, Double click – bulk records."}
                                                    placement="top" enterDelay={500} leaveDelay={200}>
                                                    <div className="stars-div-header">
                                                        <FaStar className="fa fa-star" color={(annotState.stars[item.dbkey] && annotState.stars[item.dbkey] > 0) ? '#999' : '#DDD'} onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleStars(e, item.dbkey, 1, fieldConfig, item);
                                                        }}></FaStar>
                                                        <FaStar className="fa fa-star" color={(annotState.stars[item.dbkey] && annotState.stars[item.dbkey] > 1) ? '#999' : '#DDD'} onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleStars(e, item.dbkey, 2, fieldConfig, item);
                                                        }}></FaStar>
                                                        <FaStar className="fa fa-star" color={(annotState.stars[item.dbkey] && annotState.stars[item.dbkey] > 2) ? '#999' : '#DDD'} onClick={(e) => {
                                                            e.stopPropagation();
                                                            toggleStars(e, item.dbkey, 3, fieldConfig, item);
                                                        }}></FaStar>&nbsp;
                                                    </div>
                                                </Tooltip>
                                            </div>
                                            <div className="color-select-div" onMouseLeave={() => hideColorBox(item.dbkey)}>
                                                <div className="color-select-wrapper">
                                                    <div id={'col_' + item.dbkey} className={'pull-right color-select color-' + ((annotState.colors[item.dbkey] && annotState.colors[item.dbkey] > 0) ? annotState.colors[item.dbkey] : 0)} onClick={(e) => {
                                                        e.stopPropagation();
                                                        showColorBox(e, item.dbkey);
                                                    }}></div>
                                                    <div tabIndex="0" id={'cob_' + item.dbkey} className="clearfix color-box-container1"
                                                        style={{ display: showColor ? 'block' : 'none' }}
                                                        onBlur={() => hideColorBox(item.dbkey)}
                                                        onMouseLeave={() => hideColorBox(item.dbkey)}
                                                    >
                                                        <div onClick={(e) => { selectColor(item.dbkey, 0, fieldConfig, item); e.stopPropagation(); }} className="color-0 color-box pull-left"></div>
                                                        <div onClick={(e) => { selectColor(item.dbkey, 1, fieldConfig, item); e.stopPropagation(); }} className="color-1 color-box pull-left"></div>
                                                        <div onClick={(e) => { selectColor(item.dbkey, 2, fieldConfig, item); e.stopPropagation(); }} className="color-2 color-box pull-left"></div>
                                                        <div onClick={(e) => { selectColor(item.dbkey, 3, fieldConfig, item); e.stopPropagation(); }} className="color-3 color-box pull-left"></div>
                                                        <div onClick={(e) => { selectColor(item.dbkey, 4, fieldConfig, item); e.stopPropagation(); }} className="color-4 color-box pull-left"></div>
                                                        <div onClick={(e) => { selectColor(item.dbkey, 5, fieldConfig, item); e.stopPropagation(); }} className="color-5 color-box pull-left"></div>
                                                        <div onClick={(e) => { selectColor(item.dbkey, 6, fieldConfig, item); e.stopPropagation(); }} className="color-6 color-box pull-left"></div>
                                                        <div onClick={(e) => { selectColor(item.dbkey, 7, fieldConfig, item); e.stopPropagation(); }} className="color-7 color-box pull-left"></div>
                                                        <div onClick={(e) => { selectColor(item.dbkey, 8, fieldConfig, item); e.stopPropagation(); }} className="color-8 color-box pull-left"></div>
                                                        <div onClick={(e) => { selectColor(item.dbkey, 9, fieldConfig, item); e.stopPropagation(); }} className="color-9 color-box pull-left"></div>
                                                        <div onClick={(e) => { selectColor(item.dbkey, 10, fieldConfig, item); e.stopPropagation(); }} className="color-10 color-box pull-left"></div>
                                                        <div onClick={(e) => { selectColor(item.dbkey, 11, fieldConfig, item); e.stopPropagation(); }} className="color-11 color-box pull-left"></div>
                                                        <div onClick={(e) => { selectColor(item.dbkey, 12, fieldConfig, item); e.stopPropagation(); }} className="color-12 color-box pull-left"></div>
                                                        <div onClick={(e) => { selectColor(item.dbkey, 13, fieldConfig, item); e.stopPropagation(); }} className="color-13 color-box pull-left"></div>
                                                    </div>
                                                </div>
                                            </div>

                                        </div>
                                    </div>
                                }</div>


                        </div>



                        <div className="section-header-links">
                            {(() => {
                                // Section names for regular fields
                                const sectionNames = {
                                    'SUBJECT_P1': 'Abstract',
                                    'ALI_INFO': 'Alignment',
                                    'SUBJECT_CT': 'Claims',
                                    'SUBJECT_PC': 'Classification',
                                    'SUBJECT_FAMINFO': 'Family',
                                    'PATENT_DATES': 'Dates',
                                    'LEGAL_STATUS': 'Legal Status',
                                    'ORGANISM': 'Organism',
                                    'QUERY_ALI_INFO': 'Query',
                                    'SUBJECT_ALI_INFO': 'Subject',
                                    'ASSIGNEES': 'Assignees',
                                    'CAS_RGGROUP': 'CAS RG Group',
                                    'CAS_CDGROUP': 'CAS CD Group',
                                    'GXGROUP': 'GQ ID Group',
                                    'PCT_GROUP': 'PCT Group',
                                    'UFS_DISPLAY': 'UFS',
                                    'DOCLINKS': 'Links',
                                    'SUBJECT_ZA': 'Title',
                                    'SUBJECT_ZB': 'Commercial Impact',
                                    'SUBJECT_ZC': 'Summary',
                                    'SUBJECT_ZD': 'Claim Analysis',
                                    'SUBJECT_ZE': 'SeqID table',
                                    'SUBJECT_ZF': 'AI Normalized Parent',
                                };
                                // Compose the list of columns to show as links (AI_SUMMARY_FIELDS are
                                // license-gated real field codes, not part of getFullRecordFields — see
                                // the AI_SUMMARY_FIELDS comment above)
                                let columns = [];
                                if (displayedColumns && displayedColumns.length > 0) {
                                    columns = displayedColumns.filter(col =>
                                        fieldConfig.getFullRecordFields(selectedItem.type, data.displayMode).includes(col) ||
                                        AI_SUMMARY_FIELDS.includes(col)
                                    );
                                } else {
                                    columns = fieldConfig.getFullRecordFields(selectedItem.type, data.displayMode);
                                }
                                return columns.map((col) => {
                                    if (col === 'DOCHEADER') return null;
                                    // AI Normalized Parent now renders inside the ASSIGNEES section, not as its own anchor
                                    if (col === 'SUBJECT_ZF') return null;
                                    // Regular section fields
                                    if (!sectionNames[col]) return null;
                                    // Skip certain sections if not in displayedColumns
                                    if (
                                        ['LEGAL_STATUS', 'ASSIGNEES', 'PATENT_DATES', 'PCT_GROUP', 'SUBJECT_PC', 'GXGROUP'].includes(col) &&
                                        !(displayedColumns && displayedColumns.includes(col))
                                    ) {
                                        return null;
                                    }
                                    const doc = data.docItems[selectedItem.dbkey];
                                    let showSection = false;
                                    switch (col) {
                                        case 'SUBJECT_P1':
                                            showSection = !!(doc && doc[col]);
                                            break;
                                        case 'SUBJECT_PC':
                                            showSection = !!(doc && doc[col] && Object.keys(doc[col]).length > 0);
                                            break;
                                        case 'SUBJECT_CT':
                                            showSection = !!(doc && fieldConfig.displayField(doc, col) && doc[col] && Object.keys(doc[col]).length > 0);
                                            break;
                                        case 'SUBJECT_Z3':
                                            showSection = !!(doc && fieldConfig.displayField(doc, col) && doc[col] && Object.keys(doc[col]).length > 0);
                                            break;
                                        case 'SUBJECT_CC':
                                            showSection = !!(doc && doc[col]);
                                            break;
                                        case 'SUBJECT_SEQIDNOSUMM':
                                            showSection = !!(doc && fieldConfig.displayField(doc, col));
                                            break;
                                        case 'ASSIGNEES':
                                            showSection = !!(doc && (doc['SUBJECT_PA'] || doc['SUBJECT_PU'] || doc['SUBJECT_PZ'] || doc['SUBJECT_PI']
                                                || (columns.includes('SUBJECT_ZF') && doc['SUBJECT_ZF'])));
                                            break;
                                        case 'PATENT_DATES':
                                            showSection = !!(doc && (doc['SUBJECT_D3'] || doc['SUBJECT_D4'] || doc['SUBJECT_PR'] || doc['SUBJECT_D5'] || doc['SUBJECT_D2']));
                                            break;
                                        case 'LEGAL_STATUS':
                                            showSection = !!(doc && (doc['SUBJECT_PG'] || doc['SUBJECT_PH'] || doc['SUBJECT_L3'] || doc['SUBJECT_L2']));
                                            break;
                                        case 'ORGANISM':
                                            showSection = !!(doc && (doc['SUBJECT_OS'] || doc['SUBJECT_OO'] || doc['SUBJECT_OX']));
                                            break;
                                        case 'QUERY_ALI_INFO':
                                            showSection = !!doc;
                                            break;
                                        case 'SUBJECT_ALI_INFO':
                                            showSection = !!doc;
                                            break;
                                        case 'ALI_INFO':
                                            showSection = !!doc;
                                            break;
                                        case 'UFS_DISPLAY':
                                            showSection = !!(doc && fieldConfig.displayField(doc, col));
                                            break;
                                        case 'CAS_RGGROUP':
                                            showSection = !!(doc && (doc['SUBJECT_RG'] || doc['SUBJECT_RB'] || doc['SUBJECT_RC']));
                                            break;
                                        case 'CAS_CDGROUP':
                                            showSection = !!(doc && (doc['SUBJECT_CD'] || doc['SUBJECT_CK'] || doc['SUBJECT_CL']));
                                            break;
                                        case 'GXGROUP':
                                            showSection = !!(doc && (doc['SUBJECT_GX'] || doc['UFS_DISPLAY'] || doc['SUBJECT_PJ']));
                                            break;
                                        case 'PCT_GROUP':
                                            showSection = !!(doc && (doc['SUBJECT_P4'] || doc['SUBJECT_P5'] || doc['SUBJECT_P8']));
                                            break;
                                        case 'SUBJECT_FAMINFO':
                                            showSection = !!(doc && fieldConfig.displayField(doc, col));
                                            break;
                                        case 'DOCLINKS':
                                            if (showLinksSection) {
                                                showSection = !!doc;
                                            }
                                            break;
                                        default:
                                            showSection = !!(doc && doc[col]);
                                    }
                                    if (!showSection) return null;
                                    // Find the index for the anchor
                                    const regularFields = displayedColumns && displayedColumns.length > 0
                                        ? displayedColumns.filter(c => c !== 'DOCHEADER' && (fieldConfig.getFullRecordFields(selectedItem.type, data.displayMode).includes(c) || AI_SUMMARY_FIELDS.includes(c)))
                                        : fieldConfig.getFullRecordFields(selectedItem.type, data.displayMode).filter(c => c !== 'DOCHEADER');
                                    const c = regularFields.indexOf(col);
                                    return (
                                        <a
                                            key={col + '_link'}
                                            href="#"
                                            className={AI_SUMMARY_FIELDS.includes(col) ? 'header-link-ai' : ''}
                                            style={AI_SUMMARY_FIELDS.includes(col) ? undefined : { color: '#337ab7', textDecoration: 'underline', cursor: 'pointer' }}
                                            onClick={e => {
                                                e.preventDefault();
                                                const el = document.getElementById(selectedItem.dbkey + '_details_' + col);
                                                const container = document.querySelector('.detail-container');
                                                if (el && container) {
                                                    isScrollingProgrammatically.current = true;
                                                    const header = document.querySelector('.sticky-docheader');
                                                    const headerHeight = header ? header.offsetHeight : 0;
                                                    const y = el.offsetTop - headerHeight;
                                                    container.scrollTo({ top: y, behavior: 'auto' });

                                                    if (setCurrentVisibleSection) {
                                                        setCurrentVisibleSection(col);
                                                    }

                                                    setTimeout(() => {
                                                        isScrollingProgrammatically.current = false;
                                                    }, 1000);
                                                }
                                            }}
                                        >
                                            {sectionNames[col]}
                                        </a>
                                    );
                                });
                            })()}
                        </div>
                    </div>

                </div>
            )}
            
            {uniqueColumns
                .map((col, colIdx) =>
                    (col === 'DOCLINKS' && !showLinksSection) ? null :
                        <div key={selectedItem.dbkey + '_details_' + col} className="details" id={selectedItem.dbkey + '_details_' + col}>
                            {/* Custom rendering for AI-generated columns (bulk-indexed SUBJECT_ZA/ZB/ZC, license-gated via availableColumns in ResultTable.js) */}
                            {col === 'SUBJECT_ZA' && data.docItems[selectedItem.dbkey][col] && (
                                <>
                                <div>
                                    <Tooltip title="This content has been generated using AI.">
                                        <div className="detail-label field-header ">Title &nbsp;<BsStars className="icon-star" /></div>
                                    </Tooltip>
                                </div>
                                    <div className={toggle ? "detail-header detail-spacer ai-detail-content" : "detail-spacer ai-detail-content-dark"}>{data.docItems[selectedItem.dbkey][col]}</div>
                                </>
                            )}
                            {col === 'SUBJECT_ZB' && data.docItems[selectedItem.dbkey][col] && (
                                <>
                                <div>
                                    <Tooltip title="This content has been generated using AI.">
                                        <div className="detail-label field-header">Commercial Impact &nbsp;<BsStars className="icon-star" /></div>
                                    </Tooltip></div>
                                    <div className={toggle ? "detail-spacer ai-detail-content" : "detail-spacer ai-detail-content-dark"}>{data.docItems[selectedItem.dbkey][col]}</div>
                                </>
                            )}
                            {col === 'SUBJECT_ZC' && data.docItems[selectedItem.dbkey][col] && (
                                <>
                                <div>
                                    <Tooltip title="This content has been generated using AI.">
                                        <div className="detail-label field-header">Summary &nbsp;<BsStars className="icon-star" /></div>
                                    </Tooltip></div>
                                    <div className={toggle ? "detail-spacer ai-detail-content" : "detail-spacer ai-detail-content-dark"}><ReactMarkdown remarkPlugins={[remarkGfm]}>{formatAiSummary(data.docItems[selectedItem.dbkey][col])}</ReactMarkdown></div>
                                </>
                            )}
                            {col === 'SUBJECT_ZD' && data.docItems[selectedItem.dbkey][col] && (
                                <>
                                <div>
                                    <Tooltip title="This content has been generated using AI.">
                                        <div className="detail-label field-header">Claim Analysis &nbsp;<BsStars className="icon-star" /></div>
                                    </Tooltip></div>
                                    <div className={toggle ? "detail-spacer ai-detail-content" : "detail-spacer ai-detail-content-dark"}>
                                        <ul className="claims-list">
                                        {parseAiClaimsAnalysis(data.docItems[selectedItem.dbkey][col]).map((group, index) => (
                                            <li key={index} className="claim-group-item">
                                            <div className="claim-range">
                                                <span className="claim-title">{group.title}</span>
                                                {group.claimRange && <span className="claim-ranges"> ({group.claimRange})</span>}
                                            </div>
                                            <div className="claim-description">{group.body}</div>
                                            </li>
                                        ))}
                                        </ul>
                                    </div>
                                </>
                            )}
                            {col === 'SUBJECT_ZE' && data.docItems[selectedItem.dbkey][col] && (
                                <>
                                <div>
                                    <Tooltip title="This table has been generated using AI.">
                                        <div className="detail-label field-header">SeqID table &nbsp;<BsStars className="icon-star" /></div>
                                    </Tooltip></div>
                                    <div className={toggle ? "detail-spacer ai-detail-content" : "detail-spacer ai-detail-content-dark"}>
                                        <SeqIdTable seqidTable={parseAiSeqIdSummary(data.docItems[selectedItem.dbkey][col])} />
                                    </div>
                                </>
                            )}
                            {col === 'DOCLINKS' &&
                                <div>
                                    <div class="detail-paragraph clearfix detail-spacer">
                                        {ReactHtmlParser(fieldConfig.prepLinks(data.docItems[selectedItem.dbkey]))}

                                        {data.docItems[selectedItem.dbkey].SUBJECT_PN && data.docItems[selectedItem.dbkey].VT_FT &&
                                            <div class="pull-left pad-menu-item sep-menu-item">
                                                <span className="ftLinkBlue" onClick={() => { openFtWindow(data.docItems[selectedItem.dbkey]) }}>Full Text Document</span>
                                            </div>
                                        }
                                    </div>
                                </div>
                            }
                            {col === 'SUBJECT_FAMINFO' && fieldConfig.displayField(data.docItems[selectedItem.dbkey], col) &&
                                <div className="detail-spacer">
                                    <table><tbody>
                                        <tr>
                                            <td className="detail-label">{fieldConfig.getFieldInfo('SUBJECT_PB').title}</td>
                                            <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_PB', item['SUBJECT_PB'], false))}</td>
                                        </tr>
                                        <tr>
                                            <td className="detail-label">{fieldConfig.getFieldInfo('NBFAMMEMBERS').title}</td>
                                            <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'NBFAMMEMBERS', item['NBFAMMEMBERS'], false))}</td>
                                        </tr>
                                        {data.docItems[selectedItem.dbkey]['FAMMEMBERS_IN_CURRENT'] &&
                                            <tr>
                                                <td className="detail-label">{fieldConfig.getFieldInfo('FAMMEMBERS_IN_CURRENT').title}</td>
                                                <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'FAMMEMBERS_IN_CURRENT', data.docItems[selectedItem.dbkey]['FAMMEMBERS_IN_CURRENT'], false))}</td>
                                            </tr>
                                        }
                                        {data.docItems[selectedItem.dbkey]['FAMMEMBERS_OUT_CURRENT'] &&
                                            <tr>
                                                <td className="detail-label">{fieldConfig.getFieldInfo('FAMMEMBERS_OUT_CURRENT').title}</td>
                                                <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'FAMMEMBERS_OUT_CURRENT', data.docItems[selectedItem.dbkey]['FAMMEMBERS_OUT_CURRENT'], false))}</td>
                                            </tr>
                                        }
                                        {data.docItems[selectedItem.dbkey]['FAMMEMBERS_OUT_GQ'] &&
                                            <tr>
                                                <td className="detail-label">{fieldConfig.getFieldInfo('FAMMEMBERS_OUT_GQ').title}</td>
                                                <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'FAMMEMBERS_OUT_GQ', data.docItems[selectedItem.dbkey]['FAMMEMBERS_OUT_GQ'], false))}</td>
                                            </tr>
                                        }
                                    </tbody></table>
                                </div>
                            }
                           {/*Abstract*/}
                            {col === 'SUBJECT_P1' && data.docItems[selectedItem.dbkey][col] && (
                                <div>
                                    <div className="detail-label field-header">{fieldConfig.getFieldInfo(col).title}</div>
                                    <div className="detail-spacer">
                                        {ReactHtmlParser(highlight(fieldConfig, filter, col, data.docItems[selectedItem.dbkey][col], false))}
                                    </div>
                                </div>
                            )}
                            
                            {/*Patent Classification*/}
                            {col === 'SUBJECT_PC' && data.docItems[selectedItem.dbkey][col] && Object.keys(data.docItems[selectedItem.dbkey][col]).length > 0 &&
                                <div>
                                    <div className="detail-label field-header">{fieldConfig.getFieldInfo(col).title}</div>
                                    <div className="detail-spacer">
                                        <table className="classification"><tbody>
                                            {Object.keys(data.docItems[selectedItem.dbkey][col]).map(
                                                (cls) => (
                                                    <tr key={'SUBJECT_PC_' + cls}>
                                                        <td className="detail-label-sub">{cls}</td>
                                                        <td>{data.docItems[selectedItem.dbkey][col][cls]}</td>
                                                    </tr>
                                                )
                                            )}
                                        </tbody></table>
                                    </div>
                                </div>
                            }

                            {/*Claim*/}
                            {col === 'SUBJECT_CT' && fieldConfig.displayField(data.docItems[selectedItem.dbkey], col)
                                && data.docItems[selectedItem.dbkey][col] && Object.keys(data.docItems[selectedItem.dbkey][col]).length > 0 &&
                                (() => {
                                    // Ensure claims section is open by default
                                    if (annotState.claimsCollapse[selectedItem.dbkey] === undefined) {
                                        annotState.claimsCollapse[selectedItem.dbkey] = false;
                                    }
                                    return (
                                        <div>
                                            <div className="detail-label-no-width field-header">
                                                {fieldConfig.getFieldInfo(col).title}
                                                <div className="expand-close-right">
                                                    {data.docItems[selectedItem.dbkey].SUBJECT_CT.length > 0 && (annotState.claimsCollapse[selectedItem.dbkey] === undefined || annotState.claimsCollapse[selectedItem.dbkey] === true) &&
                                                        <a href="#" onClick={(e) => { expandClaim(selectedItem.dbkey, false); e.preventDefault(); }}>Expand this | </a>
                                                    }
                                                    {data.docItems[selectedItem.dbkey].SUBJECT_CT.length > 0 && annotState.claimsCollapse[selectedItem.dbkey] === false &&
                                                        <span>Expand this | </span>
                                                    }

                                                    {/*<a href="#" onClick={(e) => {expandAllClaims(false);e.preventDefault();}}>Expand all</a><span> | </span>*/}
                                                    {data.docItems[selectedItem.dbkey].SUBJECT_CT.length > 0 && (annotState.claimsCollapse[selectedItem.dbkey] === undefined || annotState.claimsCollapse[selectedItem.dbkey] === true) &&
                                                        <span>Close this | </span>
                                                    }
                                                    {annotState.claimsCollapse[selectedItem.dbkey] === false &&
                                                        <a href="#" onClick={(e) => { expandClaim(selectedItem.dbkey, true); e.preventDefault(); }}>Close this | </a>
                                                    }
                                                    {/*<a href="#" onClick={(e) => {expandAllClaims(true);e.preventDefault();}}>Close all</a>*/}
                                                </div>
                                            </div>
                                            <div className="detail-spacer" >
                                                <div> {/*uib-collapse="annotState.claimsCollapse[selectedItem.dbkey]"*/}
                                                    {annotState.claimsCollapse[selectedItem.dbkey] === false && annotState.claimsCollapseLess[selectedItem.dbkey] &&
                                                        <div>
                                                            {Object.keys(data.docItems[selectedItem.dbkey].SUBJECT_CT).map(
                                                                (cls, idx) => {
                                                                    if (idx <= 3) {
                                                                        return <div key={selectedItem.dbkey + '_claim_' + cls} className="claim small-claim-container"><SeqIdAlignmentPopup html={highlight(fieldConfig, filter, col, data.docItems[selectedItem.dbkey].SUBJECT_CT[cls], false)} data={data} patentNumber={data.docItems[selectedItem.dbkey].SUBJECT_PN} /></div>;
                                                                    } else {
                                                                        return null;
                                                                    }
                                                                })}
                                                            <div className="clearfix">
                                                                <div className="pull-right">
                                                                    <a href="#" onClick={(e) => { showLessClaim(selectedItem.dbkey, false); e.preventDefault(); }}>show all claims ...</a>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    }
                                                    {annotState.claimsCollapse[selectedItem.dbkey] === false && !annotState.claimsCollapseLess[selectedItem.dbkey] &&
                                                        <div>
                                                            {Object.keys(data.docItems[selectedItem.dbkey].SUBJECT_CT).map(cls => (
                                                                <div key={selectedItem.dbkey + '_claim_' + cls} className="claim small-claim-container"><SeqIdAlignmentPopup html={highlight(fieldConfig, filter, col, data.docItems[selectedItem.dbkey].SUBJECT_CT[cls], false)} data={data} patentNumber={data.docItems[selectedItem.dbkey].SUBJECT_PN} /></div>
                                                            ))}
                                                            <div className="clearfix">
                                                                <div className="pull-right">
                                                                    {/*<a href="#" onClick={(e) => { showLessClaim(selectedItem.dbkey, true); e.preventDefault(); }}>show less claims</a>*/}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    }
                                                </div>
                                            </div>
                                        </div>);
                                })()
                            }

                            {/*Independent Claim*/}
                            {col === 'SUBJECT_Z3' && fieldConfig.displayField(data.docItems[selectedItem.dbkey], col)
                                && data.docItems[selectedItem.dbkey][col] && Object.keys(data.docItems[selectedItem.dbkey][col]).length > 0 &&
                                <div>
                                    <div className="detail-spacer" >
                                        <div>
                                            <div className="detail-label field-header">{fieldConfig.getFieldInfo(col).title}</div>
                                            <div>
                                                {Object.keys(data.docItems[selectedItem.dbkey].SUBJECT_Z3).map(cls => (
                                                    <div key={selectedItem.dbkey + '_indClaim_' + cls} className="claim small-claim-container"><SeqIdAlignmentPopup html={highlight(fieldConfig, filter, col, data.docItems[selectedItem.dbkey].SUBJECT_Z3[cls], false)} data={data} patentNumber={data.docItems[selectedItem.dbkey].SUBJECT_PN} /></div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }

                            {/*Comment*/}
                            {col === 'SUBJECT_CC' && data.docItems[selectedItem.dbkey][col] &&
                                <div>
                                    <table className="detail-spacer"><tbody>
                                        <tr>
                                            <td className="detail-label">{fieldConfig.getFieldInfo(col).title}</td>
                                            <td>{ReactHtmlParser(highlight(fieldConfig, filter, col, data.docItems[selectedItem.dbkey][col], false))}</td>
                                        </tr></tbody>
                                    </table>
                                </div>
                            }

                            {/*PSL*/}
                            {col === 'SUBJECT_SEQIDNOSUMM' && fieldConfig.displayField(data.docItems[selectedItem.dbkey], col) &&
                                <div>
                                    <table className="detail-spacer"><tbody>
                                        <tr>
                                            <td className="detail-label">{fieldConfig.getFieldInfo(col).title}</td>
                                            <td>
                                                <table className="classification"><tbody>
                                                    {data.docItems[selectedItem.dbkey].SUBJECT_SEQIDNOSUMM.Claim && Object.keys(data.docItems[selectedItem.dbkey].SUBJECT_SEQIDNOSUMM.Claim).length > 0 &&
                                                        <tr>
                                                            <td className="side-label">Claimed</td>
                                                            <td>
                                                                {Object.keys(data.docItems[selectedItem.dbkey].SUBJECT_SEQIDNOSUMM.Claim).map(
                                                                    (cls) => (
                                                                        <div key={colIdx + cls}>
                                                                            SEQ ID NOs:{cls}
                                                                            {data.docItems[selectedItem.dbkey].SUBJECT_SEQIDNOSUMM.Claim[cls] &&
                                                                                <span key={'span_' + colIdx + cls}> ({data.docItems[selectedItem.dbkey].SUBJECT_SEQIDNOSUMM.Claim[cls]})</span>
                                                                            }
                                                                        </div>
                                                                    )
                                                                )}
                                                            </td>
                                                        </tr>
                                                    }
                                                    {data.docItems[selectedItem.dbkey].SUBJECT_SEQIDNOSUMM.Disclosure && Object.keys(data.docItems[selectedItem.dbkey].SUBJECT_SEQIDNOSUMM.Disclosure).length > 0 &&
                                                        <tr>
                                                            <td className="side-label">Probable disclosure</td>
                                                            <td>
                                                                {Object.keys(data.docItems[selectedItem.dbkey].SUBJECT_SEQIDNOSUMM.Disclosure).map(
                                                                    (cls) => (
                                                                        <div key={colIdx + cls}>
                                                                            SEQ ID NOs:{cls}
                                                                            {data.docItems[selectedItem.dbkey].SUBJECT_SEQIDNOSUMM.Disclosure[cls] &&
                                                                                <span key={'span_' + colIdx + cls}> ({data.docItems[selectedItem.dbkey].SUBJECT_SEQIDNOSUMM.Disclosure[cls]})</span>
                                                                            }
                                                                            ;&nbsp;</div>
                                                                    )
                                                                )}
                                                            </td>
                                                        </tr>
                                                    }
                                                    {data.docItems[selectedItem.dbkey].SUBJECT_SEQIDNOSUMM['TBD'] && Object.keys(data.docItems[selectedItem.dbkey].SUBJECT_SEQIDNOSUMM['TBD']).length > 0 &&
                                                        <tr>
                                                            <td className="side-label">TBD</td>
                                                            <td>
                                                                {Object.keys(data.docItems[selectedItem.dbkey].SUBJECT_SEQIDNOSUMM['TBD']).map(
                                                                    (cls) => (
                                                                        <div key={colIdx + cls}>
                                                                            SEQ ID NOs:{cls}
                                                                            {data.docItems[selectedItem.dbkey].SUBJECT_SEQIDNOSUMM['TBD'][cls] &&
                                                                                <span key={'span_' + colIdx + cls}> ({data.docItems[selectedItem.dbkey].SUBJECT_SEQIDNOSUMM['TBD'][cls]})</span>
                                                                            }
                                                                            ;&nbsp;</div>
                                                                    )
                                                                )}
                                                            </td>
                                                        </tr>
                                                    }
                                                </tbody></table>
                                            </td>
                                        </tr>
                                    </tbody></table>
                                </div>
                            }

                            {/*Assignees*/}
                            {col === 'ASSIGNEES' && (data.docItems[selectedItem.dbkey]['SUBJECT_PA'] || data.docItems[selectedItem.dbkey]['SUBJECT_PU'] || data.docItems[selectedItem.dbkey]['SUBJECT_PZ']
                                || data.docItems[selectedItem.dbkey]['SUBJECT_PI']
                                || (uniqueColumns.includes('SUBJECT_ZF') && data.docItems[selectedItem.dbkey]['SUBJECT_ZF'])) &&
                                <div>
                                    <div className="detail-label field-header">{fieldConfig.getFieldInfo(col).title}</div>
                                    <div className="detail-spacer">
                                        <table className="classification"><tbody>
                                            {data.docItems[selectedItem.dbkey]['SUBJECT_PA'] &&
                                                <tr>
                                                    <td className="detail-label-sub">{fieldConfig.getFieldInfo('SUBJECT_PA').title}</td>
                                                    <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_PA', data.docItems[selectedItem.dbkey]['SUBJECT_PA'], false))}</td>
                                                </tr>
                                            }
                                            {data.docItems[selectedItem.dbkey]['SUBJECT_PU'] &&
                                                <tr>
                                                    <td className="detail-label-sub">{fieldConfig.getFieldInfo('SUBJECT_PU').title}</td>
                                                    <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_PU', data.docItems[selectedItem.dbkey]['SUBJECT_PU'], false))}</td>
                                                </tr>
                                            }
                                            {data.docItems[selectedItem.dbkey]['SUBJECT_PZ'] &&
                                                <tr>
                                                    <td className="detail-label-sub">{fieldConfig.getFieldInfo('SUBJECT_PZ').title}</td>
                                                    <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_PZ', data.docItems[selectedItem.dbkey]['SUBJECT_PZ'], false))}</td>
                                                </tr>
                                            }
                                            {uniqueColumns.includes('SUBJECT_ZF') && data.docItems[selectedItem.dbkey]['SUBJECT_ZF'] &&
                                                <tr>
                                                    <td className="detail-label-sub">
                                                        <Tooltip title="This content has been generated using AI.">
                                                            <span>{fieldConfig.getFieldInfo('SUBJECT_ZF').title} <BsStars className="icon-star" /></span>
                                                        </Tooltip>
                                                    </td>
                                                    <td>
                                                        <span className="ai-parent-name">{data.docItems[selectedItem.dbkey]['SUBJECT_ZF']}</span>
                                                        {data.docItems[selectedItem.dbkey]['SUBJECT_ZG'] && (
                                                            <span className={`ai-parent-confidence ai-parent-confidence-${data.docItems[selectedItem.dbkey]['SUBJECT_ZG'].toLowerCase()}`}>
                                                                {' '}{data.docItems[selectedItem.dbkey]['SUBJECT_ZG']} confidence
                                                            </span>
                                                        )}
                                                        {data.docItems[selectedItem.dbkey]['SUBJECT_ZH'] && (
                                                            <Tooltip title={data.docItems[selectedItem.dbkey]['SUBJECT_ZH']}>
                                                                <span className="ai-parent-why"> <FaInfoCircle className="ai-parent-why-icon" /> why?</span>
                                                            </Tooltip>
                                                        )}
                                                    </td>
                                                </tr>
                                            }
                                            {data.docItems[selectedItem.dbkey]['SUBJECT_PI'] &&
                                                <tr>
                                                    <td className="detail-label-sub">{fieldConfig.getFieldInfo('SUBJECT_PI').title}</td>
                                                    <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_PI', data.docItems[selectedItem.dbkey]['SUBJECT_PI'], false))}</td>
                                                </tr>
                                            }
                                        </tbody></table>
                                    </div>

                                </div>
                            }

                            {/*Patent Dates*/}
                            {col === 'PATENT_DATES' && (data.docItems[selectedItem.dbkey]['SUBJECT_D3'] || data.docItems[selectedItem.dbkey]['SUBJECT_D4'] || data.docItems[selectedItem.dbkey]['SUBJECT_PR']
                                || data.docItems[selectedItem.dbkey]['SUBJECT_D5'] || data.docItems[selectedItem.dbkey]['SUBJECT_D2']) &&
                                <div>
                                    <div className="detail-label field-header">{fieldConfig.getFieldInfo(col).title}</div>
                                    <div className="detail-spacer">
                                        <table className="classification"><tbody>
                                            {data.docItems[selectedItem.dbkey]['SUBJECT_D3'] && data.docItems[selectedItem.dbkey]['SUBJECT_D3'] > 0 &&
                                                <tr>
                                                    <td className="detail-label-sub">{fieldConfig.getFieldInfo('SUBJECT_D3').title}</td>
                                                    <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_D3', data.docItems[selectedItem.dbkey]['SUBJECT_D3'], false))}</td>
                                                </tr>
                                            }
                                            {data.docItems[selectedItem.dbkey]['SUBJECT_D4'] && data.docItems[selectedItem.dbkey]['SUBJECT_D4'] > 0 &&
                                                <tr>
                                                    <td className="detail-label-sub">{fieldConfig.getFieldInfo('SUBJECT_D4').title}</td>
                                                    <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_D4', data.docItems[selectedItem.dbkey]['SUBJECT_D4'], false))}</td>
                                                </tr>
                                            }
                                            {data.docItems[selectedItem.dbkey]['SUBJECT_PR'] &&
                                                <tr>
                                                    <td className="detail-label-sub">{fieldConfig.getFieldInfo('SUBJECT_PR').title}</td>
                                                    <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_PR', data.docItems[selectedItem.dbkey]['SUBJECT_PR'], false))}</td>
                                                </tr>
                                            }
                                            {data.docItems[selectedItem.dbkey]['SUBJECT_D5'] && data.docItems[selectedItem.dbkey]['SUBJECT_D5'] > 0 &&
                                                <tr>
                                                    <td className="detail-label-sub">{fieldConfig.getFieldInfo('SUBJECT_D5').title}</td>
                                                    <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_D5', data.docItems[selectedItem.dbkey]['SUBJECT_D5'], false))}</td>
                                                </tr>
                                            }
                                            {data.docItems[selectedItem.dbkey]['SUBJECT_D2'] && data.docItems[selectedItem.dbkey]['SUBJECT_D2'] > 0 &&
                                                <tr>
                                                    <td className="detail-label-sub">{fieldConfig.getFieldInfo('SUBJECT_D2').title}</td>
                                                    <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_D2', data.docItems[selectedItem.dbkey]['SUBJECT_D2'], false))}</td>
                                                </tr>
                                            }
                                        </tbody></table>
                                    </div>

                                </div>
                            }

                            {/*Legal Status*/}
                            {col === 'LEGAL_STATUS' && (data.docItems[selectedItem.dbkey]['SUBJECT_PG'] || data.docItems[selectedItem.dbkey]['SUBJECT_PH']
                                || data.docItems[selectedItem.dbkey]['SUBJECT_L3'] || data.docItems[selectedItem.dbkey]['SUBJECT_L2']) &&
                                <div>
                                    <div className="detail-label field-header">{fieldConfig.getFieldInfo(col).title}</div>
                                    <div className="detail-spacer">
                                        <table className="classification"><tbody>
                                            {data.docItems[selectedItem.dbkey]['SUBJECT_PG'] &&
                                                <tr>
                                                    <td className="detail-label-sub">{fieldConfig.getFieldInfo('SUBJECT_PG').title}</td>
                                                    <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_PG', data.docItems[selectedItem.dbkey]['SUBJECT_PG'], false))}</td>
                                                </tr>
                                            }
                                            {data.docItems[selectedItem.dbkey]['SUBJECT_PH'] &&
                                                <tr>
                                                    <td className="detail-label-sub">{fieldConfig.getFieldInfo('SUBJECT_PH').title}</td>
                                                    <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_PH', data.docItems[selectedItem.dbkey]['SUBJECT_PH'], false))}</td>
                                                </tr>
                                            }
                                            {data.docItems[selectedItem.dbkey]['SUBJECT_L3'] &&
                                                <tr>
                                                    <td className="detail-label-sub">{fieldConfig.getFieldInfo('SUBJECT_L3').title}</td>
                                                    <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_L3', data.docItems[selectedItem.dbkey]['SUBJECT_L3'], false))}</td>
                                                </tr>
                                            }
                                            {data.docItems[selectedItem.dbkey]['SUBJECT_L2'] &&
                                                <tr>
                                                    <td className="detail-label-sub">{fieldConfig.getFieldInfo('SUBJECT_L2').title}</td>
                                                    <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_L2', data.docItems[selectedItem.dbkey]['SUBJECT_L2'], false))}</td>
                                                </tr>
                                            }
                                        </tbody></table>
                                    </div>

                                </div>
                            }

                            {/*ORGANISM*/}
                            {col === 'ORGANISM' && (data.docItems[selectedItem.dbkey]['SUBJECT_OS'] || data.docItems[selectedItem.dbkey]['SUBJECT_OO'] || data.docItems[selectedItem.dbkey]['SUBJECT_OX']) &&
                                <div>
                                    <table className="detail-spacer classification"><tbody>
                                        <tr>
                                            <td className="detail-label">{fieldConfig.getFieldInfo('ORGANISM').title}</td>
                                            <td>
                                                <span>{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_OS', data.docItems[selectedItem.dbkey]['SUBJECT_OS'], false))}
                                                </span> ( <span>{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_OX', data.docItems[selectedItem.dbkey]['SUBJECT_OX'], false))}</span>)
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="detail-label-sub"></td>
                                            <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'SUBJECT_OO', data.docItems[selectedItem.dbkey]['SUBJECT_OO'], false))}</td>
                                        </tr>
                                    </tbody></table>
                                </div>
                            }

                            {/*Query info*/}
                            {col === 'QUERY_ALI_INFO' &&
                                <div>
                                    <div className="detail-label field-header">{fieldConfig.getFieldInfo(col).title}</div>
                                    <div className="detail-spacer">
                                        <table className="classification"><tbody>
                                            {
                                                QID_INFO.map(fieldId => {
                                                    if (data.docItems[selectedItem.dbkey][fieldId] || data.docItems[selectedItem.dbkey][fieldId] === 0) {
                                                        return <tr key={fieldId}>
                                                            <td className="detail-label-sub">{fieldConfig.getFieldInfo(fieldId).title}</td>
                                                            <td>{ReactHtmlParser(highlight(fieldConfig, filter, fieldId, data.docItems[selectedItem.dbkey][fieldId], false))}</td>
                                                        </tr>;
                                                    } else {
                                                        return null;
                                                    }
                                                })
                                            }
                                            <tr>
                                                <td className="detail-label-sub">Query Alignment Position</td>
                                                <td>{data.docItems[selectedItem.dbkey]['RESULT_OOBQ'] + ' - ' + data.docItems[selectedItem.dbkey]['RESULT_OOEQ']}</td>
                                            </tr>
                                        </tbody>
                                        </table>
                                    </div>
                                </div>
                            }

                            {/*Subject info*/}
                            {col === 'SUBJECT_ALI_INFO' &&
                                <div>
                                    <div className="detail-label field-header">{fieldConfig.getFieldInfo(col).title}</div>
                                    <div className="detail-spacer">
                                        <table className="classification"><tbody>
                                            {
                                                SID_INFO.map(fieldId => {
                                                    if (data.docItems[selectedItem.dbkey][fieldId] || data.docItems[selectedItem.dbkey][fieldId] === 0) {
                                                        return <tr key={fieldId}>
                                                            <td className="detail-label-sub">{fieldConfig.getFieldInfo(fieldId).title}</td>
                                                            <td>{ReactHtmlParser(highlight(fieldConfig, filter, fieldId, data.docItems[selectedItem.dbkey][fieldId], false))}</td>
                                                        </tr>;
                                                    } else { return null };
                                                })
                                            }
                                            <tr>
                                                <td className="detail-label-sub">Subject Alignment Position</td>
                                                <td>{data.docItems[selectedItem.dbkey]['RESULT_OOBD'] + ' - ' + data.docItems[selectedItem.dbkey]['RESULT_OOED']}</td>
                                            </tr>
                                            <tr>
                                                <td className="detail-label-sub">Subject Sequence Orientation</td>
                                                {data.docItems[selectedItem.dbkey].RESULT_NFQ >= 3 && data.docItems[selectedItem.dbkey].RESULT_NFQ <= 6 &&
                                                    <td>Reverse</td>
                                                }
                                                {(data.docItems[selectedItem.dbkey].RESULT_NFQ < 3 || data.docItems[selectedItem.dbkey].RESULT_NFQ > 6) &&
                                                    <td>Forward</td>
                                                }
                                            </tr>
                                        </tbody>
                                        </table>
                                    </div>
                                </div>
                            }

                            {/*Alignment*/}
                            {col === 'ALI_INFO' && (() => {

                                // Ensure alignment section is open by default
                                if (annotState.alignmentsCollapse[selectedItem.dbkey] === undefined) {
                                    annotState.alignmentsCollapse[selectedItem.dbkey] = false;
                                }
                                return (
                                    <div>
                                        <div className="detail-label-no-width field-header">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between' }}>
                                                {/*fieldConfig.getFieldInfo(col).title*/} Subject Hit Landscape
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                                                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'normal', fontSize: 12, cursor: 'pointer', marginBottom: 0 }}>
                                                        <span style={{ color: toggle ? '#555' : '#bbb' }}>Multi-Hit Mode</span>
                                                        <span style={{
                                                            position: 'relative', display: 'inline-block',
                                                            width: 34, height: 18, flexShrink: 0
                                                        }}>
                                                            <input type="checkbox" checked={multiHitMode}
                                                                onChange={e => {
                                                                    localStorage.setItem('multiHitMode', e.target.checked);
                                                                    setMultiHitMode(e.target.checked);
                                                                }}
                                                                style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
                                                            <span style={{
                                                                position: 'absolute', cursor: 'pointer',
                                                                top: 0, left: 0, right: 0, bottom: 0,
                                                                background: multiHitMode ? '#ffa726' : '#ccc',
                                                                borderRadius: 18, transition: '0.2s'
                                                            }} />
                                                            <span style={{
                                                                position: 'absolute', height: 12, width: 12,
                                                                left: multiHitMode ? 18 : 3, bottom: 3,
                                                                background: 'white', borderRadius: '50%', transition: '0.2s'
                                                            }} />
                                                        </span>
                                                    </label>
                                                    {multiHitMode && (
                                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 'normal', fontSize: 12, cursor: 'pointer', marginBottom: 0 }}>
                                                            <span style={{ color: toggle ? '#555' : '#bbb' }}>Color Mode</span>
                                                            <span style={{
                                                                position: 'relative', display: 'inline-block',
                                                                width: 34, height: 18, flexShrink: 0
                                                            }}>
                                                                <input type="checkbox" checked={alignmentColorMode}
                                                                    onChange={e => {
                                                                        localStorage.setItem('alignmentColorMode', e.target.checked);
                                                                        setAlignmentColorMode(e.target.checked);
                                                                    }}
                                                                    style={{ opacity: 0, width: 0, height: 0, position: 'absolute' }} />
                                                                <span style={{
                                                                    position: 'absolute', cursor: 'pointer',
                                                                    top: 0, left: 0, right: 0, bottom: 0,
                                                                    background: alignmentColorMode ? '#ffa726' : '#ccc',
                                                                    borderRadius: 18, transition: '0.2s'
                                                                }} />
                                                                <span style={{
                                                                    position: 'absolute', height: 12, width: 12,
                                                                    left: alignmentColorMode ? 18 : 3, bottom: 3,
                                                                    background: 'white', borderRadius: '50%', transition: '0.2s'
                                                                }} />
                                                            </span>
                                                        </label>
                                                    )}
                                                </div>
                                            </div>
                                            <div>

                                                {(() => {
                                                    if (!multiHitMode) {
                                                        const currentDoc = data?.docItems?.[selectedItem.dbkey];
                                                        return <ClassicAlignmentChart item={toChartItem(currentDoc)} />;
                                                    }

                                                    const MAX_CHART_HITS = 20;
                                                    const totalHits = subjectHitsChartRows?.length ?? 0;
                                                    const sortedRows = subjectHitsChartRows
                                                        ? [...subjectHitsChartRows].sort((a, b) => (a.RESULT_GRAPHIC?.RESULT_OOBD ?? 0) - (b.RESULT_GRAPHIC?.RESULT_OOBD ?? 0))
                                                        : null;
                                                    const displayedRows = sortedRows?.slice(0, MAX_CHART_HITS) ?? null;
                                                    const truncated = totalHits > MAX_CHART_HITS;
                                                    return (<>
                                                        <AlignmentChart
                                                            data={displayedRows}
                                                            selectedQueryId={externalSelectedQueryId}
                                                            highlightedQueryId={highlightedQueryId}
                                                            onRectHighlight={qid => {
                                                                setHighlightedQueryId(qid);
                                                            }}
                                                            queryColorMap={queryColorMap}
                                                            colorMode={alignmentColorMode}
                                                        />
                                                        {truncated && (
                                                            <div style={{
                                                                fontSize: '12px',
                                                                color: toggle ? '#d16716' : '#ffd07b',
                                                                background: toggle ? '#fffbeb' : '#3a2e15',
                                                                border: `1px solid ${toggle ? '#fcd34d' : '#a07a2a'}`,
                                                                borderRadius: 4, padding: '4px 8px', marginTop: 8
                                                            }}>
                                                                Showing first {MAX_CHART_HITS} of {totalHits} query hits for {currentSubjectSeqId}.
                                                            </div>
                                                        )}
                                                        {/* Table of query hits below the chart */}
                                                        <div style={{ marginTop: 16 }}>
                                                            {(() => {
                                                              const tableBorder = `1px solid ${toggle ? '#ccc' : '#555'}`;
                                                              const theadBg = toggle ? '#f5f5f5' : '#3a3a3a';
                                                              const theadColor = toggle ? undefined : '#e5e5e5';
                                                              return (
                                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                                                <thead>
                                                                    <tr style={{ background: theadBg, color: theadColor }}>
                                                                        <th style={{ border: tableBorder, padding: 4 }}>Query ID</th>
                                                                        <th style={{ border: tableBorder, padding: 4 }}>%Identity Query</th>
                                                                        <th style={{ border: tableBorder, padding: 4 }}>%Identity Subject</th>
                                                                        <th style={{ border: tableBorder, padding: 4 }}>Subject Position</th>
                                                                        <th style={{ border: tableBorder, padding: 4 }}>Subject Seq Orientation</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {(displayedRows || []).map((row, idx) => {
                                                                        const g = row.RESULT_GRAPHIC || {};
                                                                        const sStart = g.RESULT_OOBD;
                                                                        const sEnd = g.RESULT_OOED;
                                                                        const percentIdentitySubject = row.result_rid !== undefined ? row.result_rid : '';
                                                                        const percentIdentityQuery = row.result_riq !== undefined ? row.result_riq : '';
                                                                        const nfd = row.RESULT_NFD;
                                                                        const nfq = row.RESULT_NFQ;
                                                                        const orientationVal = nfq;
                                                                        const subjectOrientation = (orientationVal === undefined || orientationVal === null)
                                                                            ? ''
                                                                            : ((orientationVal >= 3 && orientationVal <= 6) ? 'Reverse' : 'Forward');
                                                                        const isSelected = row.query_id === externalSelectedQueryId;
                                                                        const isHighlighted = !isSelected && row.query_id === highlightedQueryId;
                                                                        const mappedColor = queryColorMap[row.query_id?.toUpperCase()];
                                                                        const rowBg = (() => {
                                                                            if (!isSelected && !isHighlighted) return undefined;
                                                                            if (alignmentColorMode) return lightenHex(mappedColor) || (isSelected ? '#cce5ff' : '#ffe0b2');
                                                                            return isSelected ? '#cce5ff' : '#ffe0b2';
                                                                        })();
                                                                        const rowBorder = alignmentColorMode && isSelected && mappedColor ? `1px solid ${mappedColor}` : undefined;
                                                                        const rowColor = (!toggle && rowBg) ? '#000' : undefined;
                                                                        return (
                                                                            <tr key={row.id || idx}
                                                                                style={{ background: rowBg, color: rowColor, cursor: isSelected ? 'default' : 'pointer', outline: rowBorder }}
                                                                                onClick={() => { if (isSelected) setHighlightedQueryId(null); else setHighlightedQueryId(row.query_id); }}
                                                                            >
                                                                                <td style={{ border: tableBorder, padding: 4 }}>{row.query_id || row.id}</td>
                                                                                <td style={{ border: tableBorder, padding: 4 }}>{percentIdentityQuery}</td>
                                                                                <td style={{ border: tableBorder, padding: 4 }}>{percentIdentitySubject}</td>
                                                                                <td style={{ border: tableBorder, padding: 4 }}>{sStart} - {sEnd}</td>
                                                                                <td style={{ border: tableBorder, padding: 4 }}>{subjectOrientation}</td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                              );
                                                            })()}
                                                        </div>
                                                    </>);
                                                })()}
                                            </div>
                                            {/* <div className="expand-close-right">
                                                {(annotState.alignmentsCollapse[selectedItem.dbkey] === undefined || annotState.alignmentsCollapse[selectedItem.dbkey] === true) &&
                                                    <a href="#" onClick={(e) => { expandAlignment(selectedItem.dbkey, false); e.preventDefault(); }}>Expand this | </a>
                                                }
                                                {annotState.alignmentsCollapse[selectedItem.dbkey] === false &&
                                                    <span>Expand this | </span>
                                                }
                                                {(annotState.alignmentsCollapse[selectedItem.dbkey] === undefined || annotState.alignmentsCollapse[selectedItem.dbkey] === true) &&
                                                    <span>Close this </span>
                                                }
                                                {annotState.alignmentsCollapse[selectedItem.dbkey] === false &&
                                                    <a href="#" onClick={(e) => { expandAlignment(selectedItem.dbkey, true); e.preventDefault(); }}>Close this </a>
                                                }
                                            </div>  */}
                                        </div>

                                        <div className={"detail-spacer-alignment " + (annotState.alignmentsCollapse[selectedItem.dbkey] === false ? "" : "hidden")}>
                                            <div>
                                                {/* <div className={"alignment-chart " + (isCmNoSeq ? "hidden" : "")} id={selectedItem.dbkey + "-alignmentchart"}><canvas width="600" height="45"></canvas></div> */}
                                                <div className="detail-label-no-width field-header"> Alignment</div>
                                                <div style={{ display: 'flex', gap: 8 }}><span style={{ minWidth: 150 }}>Query:</span><span>{data.docItems[selectedItem.dbkey]?.QUERY_ID || ''}</span></div>
                                                <div style={{ display: 'flex', gap: 8 }}><span style={{ minWidth: 150 }}>Subject:</span><span>{data.docItems[selectedItem.dbkey]?.SUBJECT_ID || data.docItems[selectedItem.dbkey]?.SUBJECT_N || ''}</span></div>
                                                <pre style={{ overflowX: 'hidden' }} className="ali-pre"><AlignmentHtmlWithTooltip html={data.gqAlignments[data.docItems[selectedItem.dbkey].dbkey]} /></pre>
                                            </div>
                                        </div>
                                        <div className="detail-spacer">
                                            <div className="detail-label-no-width field-header"> Alignment Parameters</div>
                                            {annotState.alignmentsCollapse[selectedItem.dbkey] === false &&
                                                <table className="classification"><tbody>
                                                    <tr>
                                                        <td className="detail-label-sub">Algorithm</td>
                                                        <td>{data.algorithm}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="detail-label-sub">{fieldConfig.getFieldInfo('RESULT_RL').title}</td>
                                                        <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'RESULT_RL', data.docItems[selectedItem.dbkey]['RESULT_RL'], false))}</td>
                                                    </tr>
                                                    <tr>
                                                        <td className="detail-label-sub">{fieldConfig.getFieldInfo('RESULT_RI').title}</td>
                                                        <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'RESULT_RI', data.docItems[selectedItem.dbkey]['RESULT_RI'], false))}</td>
                                                    </tr>
                                                    {data.algorithm === 'BLAST' &&
                                                        <tr>
                                                            <td className="detail-label-sub">{fieldConfig.getFieldInfo('RESULT_RE').title}</td>
                                                            <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'RESULT_RE', data.docItems[selectedItem.dbkey]['RESULT_RE'], false))}</td>
                                                        </tr>
                                                    }
                                                    {data.algorithm === 'BLAST' &&
                                                        <tr>
                                                            <td className="detail-label-sub">{fieldConfig.getFieldInfo('RESULT_RS').title}</td>
                                                            <td>{ReactHtmlParser(highlight(fieldConfig, filter, 'RESULT_RS', data.docItems[selectedItem.dbkey]['RESULT_RS'], false))}</td>
                                                        </tr>
                                                    }
                                                </tbody></table>
                                            }
                                        </div>
                                    </div>
                                );
                            })()
                            }

                            {/*UFS*/}
                            {col === 'UFS_DISPLAY' && fieldConfig.displayField(data.docItems[selectedItem.dbkey], col) &&
                                <div>
                                    <table className="detail-spacer">
                                        <tr>
                                            <td className="detail-label">{fieldConfig.getFieldInfo(col).title}</td>
                                            <td>{ReactHtmlParser(highlight(fieldConfig, filter, col, data.docItems[selectedItem.dbkey][col], false))}</td>
                                        </tr>
                                    </table>
                                </div>
                            }

                            {/*CAS RG Group*/}
                            {col === 'CAS_RGGROUP' && (data.docItems[selectedItem.dbkey]['SUBJECT_RG'] || data.docItems[selectedItem.dbkey]['SUBJECT_RB'] || data.docItems[selectedItem.dbkey]['SUBJECT_RC']) &&
                                <div>
                                    <div className="detail-spacer">
                                        <table className="classification"><tbody>
                                            {
                                                CAS_RGGROUP.map(fieldId => {
                                                    if (data.docItems[selectedItem.dbkey][fieldId]) {
                                                        return <tr key={fieldId}>
                                                            <td className="detail-label-sub" dangerouslySetInnerHTML={{ __html: fieldConfig.getFieldName(fieldId, data.algorithm, isAbWorkflow) }}></td>
                                                            <td>{ReactHtmlParser(highlight(fieldConfig, filter, fieldId, data.docItems[selectedItem.dbkey][fieldId], false))}</td>
                                                        </tr>;
                                                    } else {
                                                        return null;
                                                    }
                                                })
                                            }
                                        </tbody>
                                        </table>
                                    </div>
                                </div>
                            }

                            {/*CAS CD Group*/}
                            {col === 'CAS_CDGROUP' && (data.docItems[selectedItem.dbkey]['SUBJECT_CD'] || data.docItems[selectedItem.dbkey]['SUBJECT_CK'] || data.docItems[selectedItem.dbkey]['SUBJECT_CL']) &&
                                <div>
                                    <div className="detail-spacer">
                                        <table className="classification"><tbody>
                                            {
                                                CAS_CDGROUP.map(fieldId => {
                                                    if (data.docItems[selectedItem.dbkey][fieldId]) {
                                                        return <tr key={fieldId}>
                                                            <td className="detail-label-sub">{fieldConfig.getFieldInfo(fieldId).title}</td>
                                                            <td>{ReactHtmlParser(highlight(fieldConfig, filter, fieldId, data.docItems[selectedItem.dbkey][fieldId], false))}</td>
                                                        </tr>;
                                                    } else {
                                                        return null;
                                                    }
                                                })
                                            }
                                        </tbody>
                                        </table>
                                    </div>
                                </div>
                            }

                            {/*GQ ID Group*/}
                            {col === 'GXGROUP' && (data.docItems[selectedItem.dbkey]['SUBJECT_GX'] || data.docItems[selectedItem.dbkey]['UFS_DISPLAY'] || data.docItems[selectedItem.dbkey]['SUBJECT_PJ']) &&
                                <div>
                                    <div className="detail-spacer">
                                        <table className="classification"><tbody>
                                            {
                                                GXGROUP.map(fieldId => {
                                                    if (data.docItems[selectedItem.dbkey][fieldId]) {
                                                        return <tr key={fieldId}>
                                                            <td className="detail-label">{fieldConfig.getFieldInfo(fieldId).title}</td>
                                                            <td>{ReactHtmlParser(highlight(fieldConfig, filter, fieldId, data.docItems[selectedItem.dbkey][fieldId], false))}</td>
                                                        </tr>;
                                                    } else {
                                                        return null;
                                                    }
                                                })
                                            }
                                        </tbody>
                                        </table>
                                    </div>
                                </div>
                            }

                            {/*PCT Group*/}
                            {col === 'PCT_GROUP' && (data.docItems[selectedItem.dbkey]['SUBJECT_P4'] || data.docItems[selectedItem.dbkey]['SUBJECT_P5'] || data.docItems[selectedItem.dbkey]['SUBJECT_P8']) &&
                                <div>
                                    <div className="detail-spacer">
                                        <table className="classification"><tbody>
                                            {
                                                PCT_GROUP.map(fieldId => {
                                                    if (data.docItems[selectedItem.dbkey][fieldId]) {
                                                        return <tr key={fieldId}>
                                                            <td className="detail-label">{fieldConfig.getFieldInfo(fieldId).title}</td>
                                                            <td>{ReactHtmlParser(highlight(fieldConfig, filter, fieldId, data.docItems[selectedItem.dbkey][fieldId], false))}</td>
                                                        </tr>;
                                                    } else {
                                                        return null;
                                                    }
                                                })
                                            }
                                        </tbody>
                                        </table>
                                    </div>
                                </div>
                            }

                            {GROUPS.indexOf(col) < 0 && data.docItems[selectedItem.dbkey][col] &&
                                <div>
                                    <table className="detail-spacer"><tbody>
                                        <tr>
                                            {col === 'SUBJECT_RG' || col === 'SUBJECT_RB' || col === 'SUBJECT_RC' ?
                                                <td className="detail-label" dangerouslySetInnerHTML={{ __html: fieldConfig.getFieldInfo(col).title }}></td>
                                                : <td className="detail-label">{fieldConfig.getFieldInfo(col).title}</td>
                                            }
                                            <td>{ReactHtmlParser(highlight(fieldConfig, filter, col, data.docItems[selectedItem.dbkey][col], false))}</td>
                                        </tr>
                                    </tbody></table>
                                </div>
                            }

                        </div>
                )}

            {showAnnotationModal && <DiscoverAnnotationModal showModal={showAnnotationModal} type={type} dbkey={key} annotState={annotState} filter={filter}
                fieldConfig={fieldConfig} fromItemsPage={fromItemsPage} groupValue1={groupValue1} groupValue2={groupValue2}
                actionModal={callBack} closeModal={closeModal} value={value} parentAnnotState={parentAnnotState}
                annotGroup={annotGroup} annotGroupDisplay={annotGroupDisplay}
                subjectId={annotSubjectId} subjectPn={annotSubjectPn} />}
            {reportModal && <DiscoverReportDataIssueModal showModal={reportModal} closeModal={closeModal} patentNumber={reportPn} />}
            {renderMenu}
        </div>
    )
}
